import { useEffect, useState } from "react";
import { useLangNavigate, Link } from "@/components/LangLink";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Plus, ShieldCheck, Clock3, UserRound, X, Check, PhoneCall, PencilLine, XCircle } from "lucide-react";
import { useAuth, useLoginModal } from "@/App";
import { Reveal, Overline } from "@/components/Reveal";
import { FLAGSHIP_SERVICES, TIER_SERVICES } from "@/data/site";
import { toast } from "sonner";
import { tr, locale } from "@/i18n";
import { LucasAssistant } from "@/components/LucasAssistant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: ShieldCheck, title: "Priority Handling", text: "Your requests move to the front of the queue, every time." },
  { icon: Clock3, title: "24/7 Advisor Access", text: "Your dedicated advisor is reachable around the clock." },
  { icon: UserRound, title: "One Point of Contact", text: "Every arrangement through a single trusted advisor." },
];

const STATUS_STYLES = {
  "In Review": "text-gold border-gold/50",
  "Confirmed": "text-emerald-400 border-emerald-400/40",
  "Awaiting Payment": "text-amber-300 border-amber-300/40",
  "Cancelled": "text-red-400/90 border-red-400/40",
};

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("dc_token")}` });

const errText = (err, fallback = "Something went wrong. Please try again.") => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? tr(d) : tr(fallback);
};

function ManageModal({ open, onClose }) {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user.name, email: user.email, phone: user.phone || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const saveDetails = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/portal/update`, form, { headers: authHeader() });
      setUser(data);
      localStorage.setItem("dc_user", JSON.stringify(data));
      toast.success(tr("Your details have been updated"));
      onClose();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setError("");
    setPwSaving(true);
    try {
      await axios.post(`${API}/portal/change-password`, pw, { headers: authHeader() });
      toast.success(tr("Password changed successfully"));
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      setError(errText(err));
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="manage-account-modal"
        className="fixed inset-0 z-[70] flex items-center justify-center p-6 overflow-y-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-lg bg-[#0B0B0C]/95 backdrop-blur-xl border border-white/10 px-8 py-10 my-10"
          initial={{ y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <button onClick={onClose} data-testid="manage-modal-close" className="absolute top-5 right-5 text-white/50 hover:text-gold transition-colors" aria-label={tr("Close")}>
            <X strokeWidth={1.25} size={22} />
          </button>
          <p className="overline-lux">{tr("Manage Your Account")}</p>
          <h3 className="font-serif text-2xl font-light mt-3">{tr("Amend your details")}</h3>

          <form onSubmit={saveDetails} className="mt-8 space-y-6">
            <input data-testid="manage-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={tr("Full Name")} className="input-lux" required />
            <input data-testid="manage-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={tr("Email")} className="input-lux" required />
            <input data-testid="manage-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={tr("Phone Number")} className="input-lux" />
            <button data-testid="manage-save-button" disabled={saving} className="w-full border border-gold/70 text-gold text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold hover:text-black transition-colors duration-400 disabled:opacity-60">
              {saving ? tr("Saving…") : tr("Save Details")}
            </button>
          </form>

          <div className="my-10 border-t border-white/10" />

          <h3 className="font-serif text-2xl font-light">{tr("Change password")}</h3>
          <form onSubmit={savePassword} className="mt-8 space-y-6">
            <input data-testid="manage-current-password" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder={tr("Current Password")} className="input-lux" required />
            <input data-testid="manage-new-password" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder={tr("New Password (min. 8 characters)")} className="input-lux" required minLength={8} />
            <button data-testid="manage-password-button" disabled={pwSaving} className="w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60">
              {pwSaving ? tr("Updating…") : tr("Update Password")}
            </button>
          </form>

          {error && (
            <p data-testid="manage-error" className="mt-6 text-red-400/90 text-xs tracking-wide text-center">{error}</p>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function MembershipActionModal({ action, onClose }) {
  const { user } = useAuth();
  const [note, setNote] = useState("");
  const [tier, setTier] = useState(user.tier === "Individual" ? "Corporate" : "Individual");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  if (!action) return null;
  const isAmend = action === "amend_tier";
  const title = isAmend ? "Amend Your Membership Tier" : "Cancel Your Membership";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const finalNote = isAmend
        ? `Request to move from ${user.tier} to ${tier} membership.${note ? ` ${note}` : ""}`
        : note;
      await axios.post(`${API}/portal/membership-action`, { action, note: finalNote }, { headers: authHeader() });
      toast.success(tr("{{title}} request received", { title: tr(title) }), { description: tr("Your advisor will be in touch shortly.") });
      onClose();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="membership-action-modal"
        className="fixed inset-0 z-[70] flex items-center justify-center p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-md bg-[#0B0B0C]/95 backdrop-blur-xl border border-white/10 px-8 py-10"
          initial={{ y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <button onClick={onClose} data-testid="membership-action-close" className="absolute top-5 right-5 text-white/50 hover:text-gold transition-colors" aria-label={tr("Close")}>
            <X strokeWidth={1.25} size={22} />
          </button>
          <p className="overline-lux">{tr("Your Membership")}</p>
          <h3 className="font-serif text-2xl font-light mt-3">{tr(title)}</h3>

          <form onSubmit={submit} className="mt-8 space-y-6">
            {isAmend ? (
              <div className="flex gap-4">
                {["Individual", "Corporate"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`tier-option-${t.toLowerCase()}`}
                    onClick={() => setTier(t)}
                    className={`flex-1 border text-xs uppercase tracking-[0.25em] py-4 transition-colors duration-300 ${
                      tier === t ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/50 hover:border-white/40"
                    }`}
                  >
                    {tr(t)}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-white/55 font-light text-sm leading-relaxed">{tr("We are sorry to see you go. Your advisor will contact you to confirm the cancellation and arrange any outstanding matters.")}</p>
            )}
            <textarea
              data-testid="membership-action-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={isAmend ? "Anything we should know? (optional)" : "Reason for cancelling (optional)"}
              className="input-lux resize-none"
            />
            <button
              data-testid="membership-action-submit"
              disabled={sending}
              className={`w-full text-xs uppercase tracking-[0.3em] py-4 transition-colors duration-400 disabled:opacity-60 ${
                isAmend
                  ? "bg-gold text-black hover:bg-gold-bright"
                  : "border border-red-400/60 text-red-300 hover:bg-red-400/10"
              }`}
            >
              {sending ? tr("Submitting…") : isAmend ? tr("Request Tier Change") : tr("Confirm Cancellation")}
            </button>
            {error && <p className="text-red-400/90 text-xs tracking-wide text-center">{error}</p>}
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ServiceRequestModal({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const options = [...FLAGSHIP_SERVICES, ...(TIER_SERVICES[user.tier] || TIER_SERVICES.Individual)];
  const [form, setForm] = useState({ requested_service: "", service_date: "", preferred_time: "", location: "", guests: "", budget: "", details: "" });
  const [travel, setTravel] = useState(EMPTY_TRAVEL);
  const [travelTransport, setTravelTransport] = useState([]);
  const [yacht, setYacht] = useState(EMPTY_YACHT);
  const [jet, setJet] = useState(EMPTY_JET);
  const [security, setSecurity] = useState(EMPTY_SECURITY);
  const [supercar, setSupercar] = useState(EMPTY_SUPERCAR);
  const [chauffeur, setChauffeur] = useState(EMPTY_CHAUFFEUR);
  const [events, setEvents] = useState(EMPTY_EVENTS);
  const [wellness, setWellness] = useState(EMPTY_WELLNESS);
  const [daily, setDaily] = useState(EMPTY_DAILY);
  const [shopping, setShopping] = useState(EMPTY_SHOPPING);
  const [art, setArt] = useState(EMPTY_ART);
  const [lounge, setLounge] = useState(EMPTY_LOUNGE);
  const [club, setClub] = useState(EMPTY_CLUB);
  const [dining, setDining] = useState(EMPTY_DINING);
  const [jewellery, setJewellery] = useState(EMPTY_JEWELLERY);
  const [importExport, setImportExport] = useState(EMPTY_IMPORT_EXPORT);
  const [ieDocs, setIeDocs] = useState([]);
  const [homeAutomation, setHomeAutomation] = useState(EMPTY_HOME_AUTOMATION);
  const [education, setEducation] = useState(EMPTY_EDUCATION);
  const [corpFlight, setCorpFlight] = useState(EMPTY_CORP_FLIGHT);
  const [retreat, setRetreat] = useState(EMPTY_RETREAT);
  const [relocation, setRelocation] = useState(EMPTY_RELOCATION);
  const [relDocs, setRelDocs] = useState([]);
  const [networking, setNetworking] = useState(EMPTY_NETWORKING);
  const [incentive, setIncentive] = useState(EMPTY_INCENTIVE);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const payload = { ...form };
      if (form.requested_service === "Luxury Travel") {
        payload.travel = { ...travel, transportation_types: travel.arrange_ground === "YES" ? travelTransport : [] };
      }
      if (form.requested_service === "Yacht Charter") {
        payload.yacht = yacht;
      }
      if (form.requested_service === "Private Jet Charter") {
        payload.jet = jet;
      }
      if (form.requested_service === "Security") {
        payload.security = security;
      }
      if (form.requested_service === "Supercar Hire") {
        payload.supercar = supercar;
      }
      if (form.requested_service === "Chauffeurs & Transfers") {
        payload.chauffeur = chauffeur;
      }
      if (form.requested_service === "Events & Experiences") {
        payload.events = events;
      }
      if (form.requested_service === "Health & Wellness") {
        payload.wellness = wellness;
      }
      if (form.requested_service === "Daily Needs") {
        payload.daily = daily;
      }
      if (form.requested_service === "Personal Shopping & Sourcing") {
        payload.shopping = shopping;
      }
      if (form.requested_service === "Art & Collectibles Access") {
        payload.art = art;
      }
      if (form.requested_service === "Global Lounge / Private Terminal Access") {
        payload.lounge = lounge;
      }
      if (form.requested_service === "Elite Social Club Access") {
        payload.club = club;
      }
      if (form.requested_service === "Fine Dining") {
        payload.dining = dining;
      }
      if (form.requested_service === "Jewellery Sourcing") {
        payload.jewellery = jewellery;
      }
      if (form.requested_service === "Import & Export Management") {
        payload.import_export = importExport;
      }
      if (form.requested_service === "Home Automation & Estate Technology") {
        payload.home_automation = homeAutomation;
      }
      if (form.requested_service === "Private Education & Tutoring") {
        payload.education = education;
      }
      if (form.requested_service === "Corporate Flight Coordination") {
        payload.corp_flight = corpFlight;
      }
      if (form.requested_service === "Tailored Corporate Retreats") {
        payload.retreat = retreat;
      }
      if (form.requested_service === "Relocations / Removals") {
        payload.relocation = relocation;
      }
      if (form.requested_service === "Networking Event Planning & Access") {
        payload.networking = networking;
      }
      if (form.requested_service === "Employee & Client Incentives") {
        payload.incentive = incentive;
      }
      const { data } = await axios.post(`${API}/portal/service-requests`, payload, { headers: authHeader() });
      if (form.requested_service === "Import & Export Management" && ieDocs.length > 0 && data.id) {
        try {
          await uploadImportExportDocs(data.id, ieDocs);
        } catch {
          toast(tr("Your documents couldn't be uploaded"), { description: tr("You can add them anytime from View Request.") });
        }
      }
      if (form.requested_service === "Relocations / Removals" && relDocs.length > 0 && data.id) {
        try {
          await uploadRelocationInventory(data.id, relDocs);
        } catch {
          toast(tr("Your inventory couldn't be uploaded"), { description: tr("You can add it anytime from View Request.") });
        }
      }
      if (data.requirements_status === "failed") {
        toast(tr("Your request has been received"), { description: tr("The extra details couldn't be saved — you can complete them anytime from View Request.") });
      } else {
        toast.success(tr("Your request has been received"), { description: tr("Your advisor will be in touch shortly.") });
      }
      setForm({ requested_service: "", service_date: "", preferred_time: "", location: "", guests: "", budget: "", details: "" });
      setTravel(EMPTY_TRAVEL);
      setTravelTransport([]);
      setYacht(EMPTY_YACHT);
      setJet(EMPTY_JET);
      setSecurity(EMPTY_SECURITY);
      setSupercar(EMPTY_SUPERCAR);
      setChauffeur(EMPTY_CHAUFFEUR);
      setEvents(EMPTY_EVENTS);
      setWellness(EMPTY_WELLNESS);
      setDaily(EMPTY_DAILY);
      setShopping(EMPTY_SHOPPING);
      setArt(EMPTY_ART);
      setLounge(EMPTY_LOUNGE);
      setClub(EMPTY_CLUB);
      setDining(EMPTY_DINING);
      setJewellery(EMPTY_JEWELLERY);
      setImportExport(EMPTY_IMPORT_EXPORT);
      setIeDocs([]);
      setHomeAutomation(EMPTY_HOME_AUTOMATION);
      setEducation(EMPTY_EDUCATION);
      setCorpFlight(EMPTY_CORP_FLIGHT);
      setRetreat(EMPTY_RETREAT);
      setRelocation(EMPTY_RELOCATION);
      setRelDocs([]);
      setNetworking(EMPTY_NETWORKING);
      setIncentive(EMPTY_INCENTIVE);
      onClose();
      onCreated();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="service-request-modal"
        className="fixed inset-0 z-[70] flex items-center justify-center p-6 overflow-y-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
        <motion.div
          className="modal-scroll relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0B0B0C]/95 backdrop-blur-xl border border-white/10 px-8 py-10 my-10"
          initial={{ y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <button onClick={onClose} data-testid="service-request-close" className="absolute top-5 right-5 text-white/50 hover:text-gold transition-colors" aria-label={tr("Close")}>
            <X strokeWidth={1.25} size={22} />
          </button>
          <p className="overline-lux">{tr("Request Service")}</p>
          <h3 className="font-serif text-2xl font-light mt-3">{tr("How may we assist you?")}</h3>
          <p className="text-white/40 text-xs font-light mt-3 leading-relaxed" data-testid="service-request-identity">{tr("Submitted as")}{user.name} · {user.member_id} · {user.tier}{tr("Membership")}</p>

          <form onSubmit={submit} className="mt-8 space-y-6">
            <div className="relative">
              <select
                data-testid="service-request-service-select"
                required
                value={form.requested_service}
                onChange={set("requested_service")}
                className="input-lux appearance-none pr-8 cursor-pointer"
              >
                <option value="" className="bg-[#0A0A0B]">{tr("Requested Service")}</option>
                {options.map((o) => (
                  <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>
                ))}
              </select>
              <Plus strokeWidth={1.5} size={15} className="absolute right-0 top-4 pointer-events-none text-gold/70 rotate-45" />
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label htmlFor="sr-date" className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Service Date")}</label>
                <input id="sr-date" data-testid="service-request-date" type="date" required value={form.service_date} onChange={set("service_date")} className="input-lux cursor-pointer [color-scheme:dark]" />
              </div>
              <div>
                <label htmlFor="sr-time" className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Preferred Time")}</label>
                <input id="sr-time" data-testid="service-request-time" type="time" value={form.preferred_time} onChange={set("preferred_time")} className="input-lux cursor-pointer [color-scheme:dark]" />
              </div>
              <input data-testid="service-request-location" required value={form.location} onChange={set("location")} placeholder={tr("Location / Destination")} className="input-lux" />
              <input data-testid="service-request-guests" type="number" min="1" value={form.guests} onChange={set("guests")} placeholder={tr("Number of Guests")} className="input-lux" />
            </div>
            <input data-testid="service-request-budget" value={form.budget} onChange={set("budget")} placeholder={tr("Budget / Estimated Budget (optional)")} className="input-lux" />
            <textarea data-testid="service-request-details" required rows={4} value={form.details} onChange={set("details")} placeholder={tr("Service Request Details — tell us everything we should know…")} className="input-lux resize-none" />
            {form.requested_service === "Luxury Travel" && (
              <div className="border border-gold/20 p-6" data-testid="sr-travel-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Luxury Travel Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can begin curating your journey.")}</p>
                <TravelFields form={travel} setForm={setTravel} transport={travelTransport} setTransport={setTravelTransport} />
              </div>
            )}
            {form.requested_service === "Yacht Charter" && (
              <div className="border border-gold/20 p-6" data-testid="sr-yacht-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Yacht Charter Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can begin sourcing your yacht.")}</p>
                <YachtFields form={yacht} setForm={setYacht} />
              </div>
            )}
            {form.requested_service === "Private Jet Charter" && (
              <div className="border border-gold/20 p-6" data-testid="sr-jet-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Jet Charter Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can begin arranging your aircraft.")}</p>
                <JetFields form={jet} setForm={setJet} />
              </div>
            )}
            {form.requested_service === "Security" && (
              <div className="border border-gold/20 p-6" data-testid="sr-security-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Security Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can begin structuring your protection.")}</p>
                <SecurityFields form={security} setForm={setSecurity} />
              </div>
            )}
            {form.requested_service === "Supercar Hire" && (
              <div className="border border-gold/20 p-6" data-testid="sr-supercar-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Super Car Hire Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can begin sourcing your vehicle.")}</p>
                <SupercarFields form={supercar} setForm={setSupercar} />
              </div>
            )}
            {form.requested_service === "Chauffeurs & Transfers" && (
              <div className="border border-gold/20 p-6" data-testid="sr-chauffeur-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Chauffeur & Transfers Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can arrange your chauffeur.")}</p>
                <ChauffeurFields form={chauffeur} setForm={setChauffeur} />
              </div>
            )}
            {form.requested_service === "Events & Experiences" && (
              <div className="border border-gold/20 p-6" data-testid="sr-events-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Events & Experiences Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can begin curating your event.")}</p>
                <EventsFields form={events} setForm={setEvents} />
              </div>
            )}
            {form.requested_service === "Health & Wellness" && (
              <div className="border border-gold/20 p-6" data-testid="sr-wellness-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Health & Wellness Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can source the right practitioner for you.")}</p>
                <WellnessFields form={wellness} setForm={setWellness} />
              </div>
            )}
            {form.requested_service === "Daily Needs" && (
              <div className="border border-gold/20 p-6" data-testid="sr-daily-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Daily Needs Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can source the right people for your household.")}</p>
                <DailyNeedsFields form={daily} setForm={setDaily} />
              </div>
            )}
            {form.requested_service === "Personal Shopping & Sourcing" && (
              <div className="border border-gold/20 p-6" data-testid="sr-shopping-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Personal Shopping Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can begin sourcing on your behalf.")}</p>
                <ShoppingFields form={shopping} setForm={setShopping} />
              </div>
            )}
            {form.requested_service === "Art & Collectibles Access" && (
              <div className="border border-gold/20 p-6" data-testid="sr-art-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Art & Collectibles Access Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can begin sourcing on your behalf.")}</p>
                <ArtFields form={art} setForm={setArt} />
              </div>
            )}
            {form.requested_service === "Global Lounge / Private Terminal Access" && (
              <div className="border border-gold/20 p-6" data-testid="sr-lounge-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Global Lounge / Private Terminal Access Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can arrange everything seamlessly.")}</p>
                <LoungeFields form={lounge} setForm={setLounge} />
              </div>
            )}
            {form.requested_service === "Elite Social Club Access" && (
              <div className="border border-gold/20 p-6" data-testid="sr-club-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Elite Social Club Access Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can arrange the right access for you.")}</p>
                <ClubFields form={club} setForm={setClub} />
              </div>
            )}
            {form.requested_service === "Fine Dining" && (
              <div className="border border-gold/20 p-6" data-testid="sr-dining-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Fine Dining Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can secure the perfect table.")}</p>
                <DiningFields form={dining} setForm={setDining} />
              </div>
            )}
            {form.requested_service === "Jewellery Sourcing" && (
              <div className="border border-gold/20 p-6" data-testid="sr-jewellery-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Jewellery Sourcing Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can begin sourcing on your behalf.")}</p>
                <JewelleryFields form={jewellery} setForm={setJewellery} />
              </div>
            )}
            {form.requested_service === "Import & Export Management" && (
              <div className="border border-gold/20 p-6" data-testid="sr-import-export-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Import & Export Management Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can manage the movement for you.")}</p>
                <ImportExportFields form={importExport} setForm={setImportExport} docs={ieDocs} setDocs={setIeDocs} />
              </div>
            )}
            {form.requested_service === "Home Automation & Estate Technology" && (
              <div className="border border-gold/20 p-6" data-testid="sr-home-automation-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Home Automation & Estate Technology Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can scope the project for you.")}</p>
                <HomeAutomationFields form={homeAutomation} setForm={setHomeAutomation} />
              </div>
            )}
            {form.requested_service === "Private Education & Tutoring" && (
              <div className="border border-gold/20 p-6" data-testid="sr-education-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Private Education & Tutoring Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can match the right educational support.")}</p>
                <EducationFields form={education} setForm={setEducation} />
              </div>
            )}
            {form.requested_service === "Corporate Flight Coordination" && (
              <div className="border border-gold/20 p-6" data-testid="sr-corp-flight-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Corporate Flight Coordination Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can coordinate the journey.")}</p>
                <CorpFlightFields form={corpFlight} setForm={setCorpFlight} />
              </div>
            )}
            {form.requested_service === "Tailored Corporate Retreats" && (
              <div className="border border-gold/20 p-6" data-testid="sr-retreat-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Tailored Corporate Retreats Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can design the retreat around your objectives.")}</p>
                <RetreatFields form={retreat} setForm={setRetreat} />
              </div>
            )}
            {form.requested_service === "Relocations / Removals" && (
              <div className="border border-gold/20 p-6" data-testid="sr-relocation-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Relocations / Removals Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can manage the move for you.")}</p>
                <RelocationFields form={relocation} setForm={setRelocation} docs={relDocs} setDocs={setRelDocs} />
              </div>
            )}
            {form.requested_service === "Networking Event Planning & Access" && (
              <div className="border border-gold/20 p-6" data-testid="sr-networking-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Networking Event Planning & Access Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can plan or secure the right access for you.")}</p>
                <NetworkingFields form={networking} setForm={setNetworking} />
              </div>
            )}
            {form.requested_service === "Employee & Client Incentives" && (
              <div className="border border-gold/20 p-6" data-testid="sr-incentive-section">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Employee & Client Incentives Requirements")}</p>
                <p className="text-white/45 text-xs font-light mb-6 leading-relaxed">{tr("A few final details so we can design the incentive around your goals.")}</p>
                <IncentiveFields form={incentive} setForm={setIncentive} />
              </div>
            )}
            <button
              data-testid="service-request-submit"
              disabled={sending}
              className="w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
            >
              {sending ? tr("Submitting…") : tr("Submit Request")}
            </button>
            {error && <p data-testid="service-request-error" className="text-red-400/90 text-xs tracking-wide text-center">{error}</p>}
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const TRAVEL_TYPES = ["LEISURE", "BUSINESS GETAWAY", "FAMILY TRIP", "GROUP TRIP", "HONEYMOON", "OTHER"];
const ACCOMMODATION_TYPES = ["HOTEL", "VILLA", "HOUSE", "APARTMENT", "CRUISES", "OTHER"];
const FLIGHT_TYPES = ["PRIVATE", "FIRST-CLASS COMMERCIAL", "BUSINESS-CLASS COMMERCIAL", "ECONOMY COMMERCIAL"];
const TRANSPORT_TYPES = ["AIRPORT TRANSFER", "SUPER CAR HIRE", "PRIVATE SECURITY / DRIVER", "DAILY CHAUFFEUR"];

function TravelFields({ form, setForm, transport, setTransport }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const nights = form.date_of_departure && form.date_of_return
    ? Math.max(0, Math.round((new Date(form.date_of_return) - new Date(form.date_of_departure)) / 86400000))
    : null;
  const toggleTransport = (t) => setTransport(transport.includes(t) ? transport.filter((x) => x !== t) : [...transport, t]);

  const sel = (label, key, options, required = true) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select
        data-testid={`travel-${key}`}
        required={required}
        value={form[key]}
        onChange={set(key)}
        className="input-lux appearance-none pr-8 cursor-pointer"
      >
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  const dateField = (label, key, testId) => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} type="date" required value={form[key]} onChange={set(key)} className="input-lux cursor-pointer [color-scheme:dark]" />
    </div>
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        {sel("Travel Type", "travel_type", TRAVEL_TYPES)}
        {sel("Accommodation Type", "accommodation_type", ACCOMMODATION_TYPES)}
        {dateField("Date of Departure", "date_of_departure", "travel-departure")}
        {dateField("Date of Return", "date_of_return", "travel-return")}
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Number of Nights")}</label>
          <p data-testid="travel-nights" className="input-lux text-gold/90">{nights !== null && nights > 0 ? tr(nights === 1 ? "{{total}} night" : "{{total}} nights", { total: nights }) : tr("Calculated from your dates")}</p>
        </div>
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Number of Rooms")}</label>
          <input data-testid="travel-rooms" type="number" min="1" value={form.number_of_rooms} onChange={set("number_of_rooms")} className="input-lux" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Accommodation Budget Per Night (optional)")}</label>
          <input data-testid="travel-budget-night" value={form.budget_per_night} onChange={set("budget_per_night")} placeholder={tr("e.g. EUR 2,000")} className="input-lux" />
        </div>
        {sel("Would you like us to arrange your flights?", "arrange_flights", ["YES", "NO"])}
        {sel("Would you like us to arrange ground transportation?", "arrange_ground", ["YES", "NO"])}
        {form.arrange_flights === "YES" && (
          <>
            {sel("Flight Type", "flight_type", FLIGHT_TYPES)}
            <div>
              <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Departure Airport")}</label>
              <input data-testid="travel-dep-airport" required value={form.departure_airport} onChange={set("departure_airport")} placeholder={tr("e.g. London Heathrow")} className="input-lux" />
            </div>
            <div>
              <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Arrival Airport")}</label>
              <input data-testid="travel-arr-airport" required value={form.arrival_airport} onChange={set("arrival_airport")} placeholder={tr("e.g. Nice Côte d'Azur")} className="input-lux" />
            </div>
            <div>
              <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Preferred Airlines")}</label>
              <input data-testid="travel-airlines" value={form.preferred_airlines} onChange={set("preferred_airlines")} placeholder={tr("e.g. Emirates, British Airways")} className="input-lux" />
            </div>
          </>
        )}
        {form.arrange_ground === "YES" && (
          <>
            <div className="sm:col-span-2">
              <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-3">{tr("Types of Transportation Needed")}</label>
              <div className="flex flex-wrap gap-2.5" data-testid="travel-transport-options">
                {TRANSPORT_TYPES.map((t) => (
                  <button
                    type="button"
                    key={t}
                    data-testid={`travel-transport-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => toggleTransport(t)}
                    className={`border text-[0.6rem] uppercase tracking-[0.18em] px-3.5 py-2.5 transition-colors duration-300 ${
                      transport.includes(t) ? "border-gold/60 bg-gold/10 text-gold" : "border-white/15 text-white/60 hover:border-gold/40 hover:text-white/90"
                    }`}
                  >
                    {tr(t)}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Preferred Vehicle")}</label>
              <input data-testid="travel-vehicle" value={form.preferred_vehicle} onChange={set("preferred_vehicle")} placeholder={tr("e.g. Range Rover Autobiography")} className="input-lux" />
            </div>
          </>
        )}
      </div>
      <div className="mt-6">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Notes")}</label>
        <textarea data-testid="travel-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Anything else we should know…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_TRAVEL = {
  travel_type: "", date_of_departure: "", date_of_return: "", accommodation_type: "",
  number_of_rooms: "", budget_per_night: "", arrange_flights: "", flight_type: "",
  departure_airport: "", arrival_airport: "", preferred_airlines: "",
  arrange_ground: "", preferred_vehicle: "", notes: "",
};

function TravelRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_TRAVEL);
  const [transport, setTransport] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const { data } = await axios.put(
        `${API}/portal/service-requests/${request.id}/travel-requirements`,
        { ...form, transportation_types: form.arrange_ground === "YES" ? transport : [] },
        { headers: authHeader() }
      );
      toast.success(tr("Your travel requirements have been received"));
      onSubmitted(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="travel-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Luxury Travel Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can begin curating your journey. Your destination, party size and overall budget are already noted from your request.")}</p>
      <TravelFields form={form} setForm={setForm} transport={transport} setTransport={setTransport} />
      <button
        data-testid="travel-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Travel Requirements")}
      </button>
      {error && <p data-testid="travel-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

function YachtFields({ form, setForm }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const days = form.charter_start && form.charter_end
    ? Math.max(0, Math.round((new Date(form.charter_end) - new Date(form.charter_start)) / 86400000))
    : null;

  const field = (label, key, testId, placeholder) => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={["cruising_area", "charter_start", "charter_end"].includes(key)} type={key.startsWith("charter_") ? "date" : "text"} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${key.startsWith("charter_") ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Charter Details")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        <div className="sm:col-span-2">{field("Preferred Cruising Area / Itinerary", "cruising_area", "yacht-cruising-area", "e.g. French Riviera — Monaco to Saint-Tropez")}</div>
        {field("Charter Start Date", "charter_start", "yacht-start")}
        {field("Charter End Date", "charter_end", "yacht-end")}
        {days !== null && days > 0 && (
          <p className="sm:col-span-2 text-gold/80 text-xs tracking-wide" data-testid="yacht-days">{tr("Charter duration: {{total}} days", { total: days })}</p>
        )}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Yacht Preferences")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Preferred Yacht Size", "yacht_size", "yacht-size", "e.g. 50–60 metres")}
        {field("Yacht Type / Style", "yacht_style", "yacht-style", "e.g. Motor yacht, classic sailing")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Onboard Requirements")}</p>
      <textarea data-testid="yacht-onboard" rows={3} value={form.onboard_requests} onChange={set("onboard_requests")} placeholder={tr("Crew preferences, chef, water toys, celebrations…")} className="input-lux resize-none" />
    </>
  );
}

const EMPTY_YACHT = { cruising_area: "", charter_start: "", charter_end: "", yacht_size: "", yacht_style: "", onboard_requests: "" };

const JET_TRIP_TYPES = ["ONE-WAY", "ROUND TRIP", "MULTI-LEG", "EMPTY LEG"];
const AIRCRAFT_CATEGORIES = ["LIGHT JET", "MIDSIZD JET", "SUPERMIDSIZED JET", "HEAVY JET", "ULTRA LONG RANGE JET"];

function JetFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "trip_type" && e.target.value !== "ROUND TRIP") next.date_of_return = "";
    setForm(next);
  };

  const sel = (label, key, options, testId) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  const field = (label, key, testId, placeholder, type = "text", required = true) => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type === "date" ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  return (
    <>
      {sel("Trip Type", "trip_type", JET_TRIP_TYPES, "jet-trip-type")}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mt-6">
        {field("Date of Departure", "date_of_departure", "jet-departure", "", "date")}
        {form.trip_type === "ROUND TRIP" && field("Date of Return", "date_of_return", "jet-return", "", "date")}
        {field("Departure Airport", "departure_airport", "jet-dep-airport", "e.g. London Luton")}
        {field("Arrival Airport", "arrival_airport", "jet-arr-airport", "e.g. Nice Côte d'Azur")}
        {field("Number of Luggage", "number_of_luggage", "jet-luggage", "e.g. 6", "number", false)}
        {field("Preferred Aircraft Type", "preferred_aircraft_type", "jet-aircraft-type", "e.g. Gulfstream G650", "text", false)}
        {sel("Aircraft Category", "aircraft_category", AIRCRAFT_CATEGORIES, "jet-aircraft-category")}
        {sel("Cabin Crew Required", "cabin_crew", ["yes", "no"], "jet-cabin-crew")}
        <div className="sm:col-span-2">{field("Catering / Onboard Amenities", "catering", "jet-catering", "e.g. Champagne service, specific dietary requirements", "text", false)}</div>
        {sel("Ground Transport Required", "ground_transport", ["YES", "NO"], "jet-ground-transport")}
      </div>
    </>
  );
}

const EMPTY_JET = { trip_type: "", date_of_departure: "", departure_airport: "", arrival_airport: "", date_of_return: "", number_of_luggage: "", preferred_aircraft_type: "", aircraft_category: "", cabin_crew: "", catering: "", ground_transport: "" };

const SECURITY_TYPES = [
  "MOBILE PATROL", "EXECUTIVE PROTECTION", "SECURITY GUARDS", "AIRPORT SECURITY", "EVENT SECURITY",
  "K9 SECURITY", "PATROL GUARDS", "ARMED SECURITY", "EVENT MANAGEMENT", "SURVEILLANCE TEAMS",
  "SECURITY CONSULTING", "DIGITAL SECURITY", "CCTV MONITORING", "CLOSE PROTECTION (CP)", "BODYGUARD",
  "ASSET MANAGEMENT", "RISK ASSESSMENT",
];
const THREAT_LEVELS = [
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
  { value: "NOT SURE (DELUX RECOMMENDATION)", label: "Not Sure — Delux to Recommend" },
];

function SecurityFields({ form, setForm }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const sel = (label, key, testId, children) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        {children}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        {sel("Type of Security", "security_type", "security-type", (
          <>
            <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
            {SECURITY_TYPES.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
          </>
        ))}
        {sel("Threat Level", "threat_level", "security-threat", (
          <>
            <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
            {THREAT_LEVELS.map((o) => <option key={o.value} value={o.value} className="bg-[#0A0A0B]">{tr(o.label)}</option>)}
          </>
        ))}
        <div className="sm:col-span-2">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Duration")}</label>
          <input data-testid="security-duration" value={form.duration} onChange={set("duration")} placeholder={tr("e.g. 3 evenings, 2 weeks, ongoing")} className="input-lux" />
        </div>
      </div>
      <div className="mt-6">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Information")}</label>
        <textarea data-testid="security-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Any context that will help us assess and fulfil your requirement…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_SECURITY = { security_type: "", threat_level: "", duration: "", notes: "" };

const SUPERCAR_MAKES = ["FERRARI", "LAMBORGHINI", "PORSCHE", "McLAREN", "ASTON MARTIN", "ROLLS ROYCE", "BENTLEY", "MASERATI", "BUGATTI", "PAGANI", "KOENIGSEGG", "BMW", "MERCEDES", "AUDI", "OTHER"];
const MILEAGE_OPTIONS = [
  { value: "LIMITED", label: "Limited" },
  { value: "UNLIMITED", label: "Unlimited" },
  { value: "EXTENDED", label: "Extended" },
  { value: "NOT SURE (D RECOMMENDATION)", label: "Not Sure — Delux to Recommend" },
];
const COLLECTION_OPTIONS = ["DELIVERY", "COLLECTION"];

function SupercarFields({ form, setForm }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const sel = (label, key, testId, children, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        {children}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Licence Issue Country")}</label>
          <input data-testid="supercar-licence" required value={form.licence_country} onChange={set("licence_country")} placeholder={tr("e.g. United Kingdom")} className="input-lux" />
        </div>
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Number of Vehicles Required")}</label>
          <input data-testid="supercar-vehicles" type="number" min="1" required value={form.vehicles_required} onChange={set("vehicles_required")} className="input-lux" />
        </div>
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Rental Start Date")}</label>
          <input data-testid="supercar-start" type="date" required value={form.rental_start} onChange={set("rental_start")} className="input-lux cursor-pointer [color-scheme:dark]" />
        </div>
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Rental End Date")}</label>
          <input data-testid="supercar-end" type="date" required min={form.rental_start || undefined} value={form.rental_end} onChange={set("rental_end")} className="input-lux cursor-pointer [color-scheme:dark]" />
        </div>
        {sel("Vehicle Make", "vehicle_make", "supercar-make", (
          <>
            <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
            {SUPERCAR_MAKES.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
          </>
        ))}
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Vehicle Model")}</label>
          <input data-testid="supercar-model" required={form.vehicle_make === "OTHER"} value={form.vehicle_model} onChange={set("vehicle_model")} placeholder={form.vehicle_make === "OTHER" ? "Tell us the vehicle you have in mind" : "e.g. 296 GTB"} className="input-lux" />
        </div>
        {sel("Mileage Preferences", "mileage", "supercar-mileage", (
          <>
            <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
            {MILEAGE_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-[#0A0A0B]">{tr(o.label)}</option>)}
          </>
        ))}
        {sel("Collection Options", "collection", "supercar-collection", (
          <>
            <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
            {COLLECTION_OPTIONS.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
          </>
        ))}
        <div className="sm:col-span-2">
          {sel("Would you like Delux Concierge assistance with your vehicle requirements?", "concierge_assistance", "supercar-concierge", (
            <>
              <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
              <option value="YES" className="bg-[#0A0A0B]">{tr("Yes")}</option>
              <option value="NO" className="bg-[#0A0A0B]">{tr("No")}</option>
            </>
          ))}
        </div>
      </div>
      <div className="mt-6">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Notes")}</label>
        <textarea data-testid="supercar-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Anything else we should know…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_SUPERCAR = { licence_country: "", rental_start: "", rental_end: "", mileage: "", collection: "", vehicles_required: "", vehicle_make: "", vehicle_model: "", concierge_assistance: "", notes: "" };

const CHAUFFEUR_TRIP_TYPES = ["ONE-WAY", "ROUND TRIP", "HOURLY HIRE"];
const CHAUFFEUR_VEHICLES = [
  "ROLLS ROYCE GHOST", "ROLLS ROYCE PHANTOM", "ROLLS ROYCE CULLINAN", "BELTLEY FLYING SPUR",
  "BENTLEY MULSANNE", "BENTLEY BENTAYGA", "BMW I7", "BMW 7 SERIES", "MERCEDES-BENZ S-CLASS MAYBACH",
  "MERCEDES-BENZ S-CLASS STANDARD", "MERCEDES-BENZ E-CLASS", "MERCEDES-BENZ V-CLASS JETLINER/MAYBACH",
  "MERCEDES-BENZ V-CLASS STANDARD", "AUDI A5", "AUDI A8", "TESLA MODEL X", "TESLA MODEL S",
  "RANGE ROVER AUTOBIOGRAPHY", "LUXURY MINIBUS",
];

function ChauffeurFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "trip_type" && e.target.value !== "HOURLY HIRE") next.number_of_hours = "";
    if (k === "return_trip" && e.target.value === "NO") { next.date_of_return = ""; next.return_pickup_address = ""; }
    setForm(next);
  };

  const sel = (label, key, testId, options, required = true) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  const field = (label, key, testId, placeholder, type = "text", required = true, extra = {}) => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type === "date" ? " cursor-pointer [color-scheme:dark]" : ""}`} {...extra} />
    </div>
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        {sel("Trip Type", "trip_type", "chauffeur-trip-type", CHAUFFEUR_TRIP_TYPES)}
        {field("Date of Pick-Up", "date_of_pickup", "chauffeur-pickup-date", "", "date")}
        {form.trip_type === "HOURLY HIRE" && field("Number of Hours", "number_of_hours", "chauffeur-hours", "e.g. 4", "number", true, { min: 1 })}
        {field("Flight Number (if applicable)", "flight_number", "chauffeur-flight", "e.g. BA318", "text", false)}
        <div className="sm:col-span-2">{field("Pick-Up Address", "pickup_address", "chauffeur-pickup", "e.g. The Connaught, Carlos Place, London")}</div>
        <div className="sm:col-span-2">{field("Drop-Off Address", "dropoff_address", "chauffeur-dropoff", "e.g. London Heathrow Terminal 5")}</div>
        {sel("Return Trip Required?", "return_trip", "chauffeur-return-trip", ["YES", "NO"])}
        {form.return_trip === "YES" && (
          <>
            {field("Date of Return", "date_of_return", "chauffeur-return-date", "", "date", true, { min: form.date_of_pickup || undefined })}
            {field("Return Pick-Up Address", "return_pickup_address", "chauffeur-return-pickup", "e.g. Hotel Martinez, Cannes")}
          </>
        )}
        {field("Number of Luggage", "number_of_luggage", "chauffeur-luggage", "e.g. 4", "number", false, { min: 0 })}
        {field("Luggage Size", "luggage_size", "chauffeur-luggage-size", "e.g. 2 large cases, 2 carry-ons", "text", false)}
        <div className="sm:col-span-2">{sel("Vehicle", "vehicle", "chauffeur-vehicle", CHAUFFEUR_VEHICLES, false)}</div>
      </div>
      <div className="mt-6">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Special Requests")}</label>
        <textarea data-testid="chauffeur-notes" rows={3} value={form.special_requests} onChange={set("special_requests")} placeholder={tr("Child seats, meet & greet, signage, accessibility, additional stops, special occasions…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_CHAUFFEUR = { trip_type: "", date_of_pickup: "", number_of_hours: "", flight_number: "", pickup_address: "", dropoff_address: "", return_trip: "", date_of_return: "", return_pickup_address: "", number_of_luggage: "", luggage_size: "", vehicle: "", special_requests: "" };

const EVENT_TYPES = ["WEDDINGS", "CONCERTS", "SPORTING EVENTS", "PRIVATE EVENTS", "CORPORATE EVENTS", "OTHER"];

function EventsFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "event_type" && e.target.value !== "OTHER") next.other_event_type = "";
    setForm(next);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        <div className="relative">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Event Type")}</label>
          <select data-testid="events-type" required value={form.event_type} onChange={set("event_type")} className="input-lux appearance-none pr-8 cursor-pointer">
            <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
            {EVENT_TYPES.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
          </select>
          <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
        </div>
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Date of Event")}</label>
          <input data-testid="events-date" type="date" required value={form.date_of_event} onChange={set("date_of_event")} className="input-lux cursor-pointer [color-scheme:dark]" />
        </div>
        {form.event_type === "OTHER" && (
          <div className="sm:col-span-2">
            <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Other Event Type")}</label>
            <input data-testid="events-other" required value={form.other_event_type} onChange={set("other_event_type")} placeholder={tr("Tell us the type of event")} className="input-lux" />
          </div>
        )}
      </div>
      <div className="mt-6">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Event Requirements / Details")}</label>
        <textarea data-testid="events-details" required rows={4} value={form.event_details} onChange={set("event_details")} placeholder={tr("Type of experience, venue requirements, catering, entertainment, production / AV, guest experience, branding, special arrangements…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_EVENTS = { event_type: "", other_event_type: "", date_of_event: "", event_details: "" };

const WELLNESS_SERVICE_TYPES = [
  "PERSONAL TRAINER", "PRIVATE YOGA OR PILATES INSTRUCTOR", "SPA OR MASSAGE THERAPIST",
  "NUTRITIONIST OR DIETITIAN", "WELLNESS RETREATS", "BEAUTY & AESTHETICIANS", "OTHER",
];

function WellnessFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "service_type" && e.target.value !== "OTHER") next.other_service = "";
    setForm(next);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        <div className="relative">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Type of Service")}</label>
          <select data-testid="wellness-type" required value={form.service_type} onChange={set("service_type")} className="input-lux appearance-none pr-8 cursor-pointer">
            <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
            {WELLNESS_SERVICE_TYPES.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
          </select>
          <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
        </div>
        {form.service_type === "OTHER" && (
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Other Service")}</label>
            <input data-testid="wellness-other" required value={form.other_service} onChange={set("other_service")} placeholder={tr("Tell us the service you require")} className="input-lux" />
          </div>
        )}
        <div className="sm:col-span-2">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Gender Preference for Trainer / Practitioner")}</label>
          <input data-testid="wellness-gender" value={form.gender_preference} onChange={set("gender_preference")} placeholder={tr("e.g. Female, Male, No preference")} className="input-lux" />
        </div>
      </div>
      <div className="mt-6">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Special Requests")}</label>
        <textarea data-testid="wellness-requests" rows={3} value={form.special_requests} onChange={set("special_requests")} placeholder={tr("Any preferences or requirements relevant to your request…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_WELLNESS = { service_type: "", other_service: "", gender_preference: "", special_requests: "" };

const DAILY_NEEDS_SERVICES = ["NANNIES", "PRIVATE CHEFS", "BUTLER", "HOUSE KEEPERS", "HOUSE MANAGER", "TUTORS", "CHAUFFEUR DRIVERS", "SECURITY"];
const WORKING_ARRANGEMENTS = ["LIVE IN", "DAY-TIME ONLY", "EVENINGS ONLY", "HOURLY HIRE"];

function DailyNeedsFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "working_arrangement" && e.target.value !== "HOURLY HIRE") next.number_of_hours = "";
    setForm(next);
  };

  const sel = (label, key, testId, options) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        {sel("Required Service", "required_service", "daily-service", DAILY_NEEDS_SERVICES)}
        {sel("Working Arrangement", "working_arrangement", "daily-arrangement", WORKING_ARRANGEMENTS)}
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Start Date")}</label>
          <input data-testid="daily-start" type="date" required value={form.start_date} onChange={set("start_date")} className="input-lux cursor-pointer [color-scheme:dark]" />
        </div>
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("End Date")}</label>
          <input data-testid="daily-end" type="date" required min={form.start_date || undefined} value={form.end_date} onChange={set("end_date")} className="input-lux cursor-pointer [color-scheme:dark]" />
        </div>
        {form.working_arrangement === "HOURLY HIRE" && (
          <div className="sm:col-span-2">
            <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Number of Hours")}</label>
            <input data-testid="daily-hours" type="number" min="1" required value={form.number_of_hours} onChange={set("number_of_hours")} placeholder={tr("e.g. 20")} className="input-lux" />
          </div>
        )}
      </div>
      <div className="mt-6">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Special Requests")}</label>
        <textarea data-testid="daily-requests" rows={3} value={form.special_requests} onChange={set("special_requests")} placeholder={tr("Duties, experience, schedule requirements, household expectations, qualifications…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_DAILY = { required_service: "", start_date: "", end_date: "", working_arrangement: "", number_of_hours: "", special_requests: "" };

const SHOPPING_FREQUENCIES = ["ONE-OFF", "SEASONAL", "ONGOING / MONTHLY"];

function ShoppingFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "occasion" && e.target.value === "NO") next.occasion_detail = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false, type = "text") => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type === "date" ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Shopping Requirements")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        <div className="sm:col-span-2">{field("Type of Shopping Required", "shopping_type", "shopping-type", "e.g. Fashion & wardrobe, fine jewellery, gifts", true)}</div>
        {field("Preferred Brands", "preferred_brands", "shopping-brands", "e.g. Hermès, Cartier, Brunello Cucinelli")}
        {field("Preferred Stores", "preferred_stores", "shopping-stores", "e.g. Harrods, Avenue Montaigne")}
        <div className="sm:col-span-2">{field("Style Preferences", "style_preferences", "shopping-style", "Your aesthetic, sizing, tastes…")}</div>
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Budget & Frequency")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Budget Range for Items", "budget_range", "shopping-budget", "e.g. GBP 5,000 – 10,000")}
        {sel("Frequency", "frequency", "shopping-frequency", SHOPPING_FREQUENCIES)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Shopping Location")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Preferred Shopping Location / Market", "shopping_location", "shopping-location", "e.g. Paris, Milan, Dubai")}
        {field("Required By / Deadline", "deadline", "shopping-deadline", "", false, "date")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        {sel("Is this shopping for a specific occasion?", "occasion", "shopping-occasion", ["YES", "NO"], true)}
        {form.occasion === "YES" && field("Tell us about the occasion", "occasion_detail", "shopping-occasion-detail", "e.g. 40th birthday celebration", true)}
      </div>
      <div className="mt-6">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Notes")}</label>
        <textarea data-testid="shopping-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Anything else we should know…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_SHOPPING = { shopping_type: "", preferred_brands: "", preferred_stores: "", style_preferences: "", budget_range: "", frequency: "", shopping_location: "", deadline: "", occasion: "", occasion_detail: "", notes: "" };

function ShoppingRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_SHOPPING);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/shopping-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your shopping requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="shopping-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Personal Shopping Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can begin sourcing for you. Your general location, party size and overall budget are already noted from your request.")}</p>
      <ShoppingFields form={form} setForm={setForm} />
      <button
        data-testid="shopping-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Shopping Requirements")}
      </button>
      {error && <p data-testid="shopping-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const ART_PURPOSES = ["PERSONAL COLLECTION", "INVESTMENT", "GIFTING", "CORPORATE DISPLAY", "GALLERY EXPANSION", "OTHER"];
const ART_FREQUENCIES = ["ONE-OFF", "REGULARLY", "ONGOING/SOURCING"];
const ART_REGIONS = ["EUROPE", "ASIA", "US", "MIDDLE EAST", "NO PREFERENCES"];
const ART_DELIVERY_METHODS = ["DIRECT TO RESIDENCE", "STORAGE", "IN-PERSON COLLECTION"];
const ART_CONFIDENTIALITY = ["STANDARD", "HIGH-PROFILE", "ULTRA-PRIVATE"];

function ArtFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "purpose" && e.target.value !== "OTHER") next.purpose_other = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false) => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className="input-lux" />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("The Acquisition")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        <div className="sm:col-span-2">{field("Type of Art Requested", "art_type", "art-type", "e.g. Contemporary painting, sculpture, rare collectibles", true)}</div>
        <div className="sm:col-span-2">{field("Preferred Period / Style", "period_style", "art-period-style", "e.g. Impressionist, mid-century modern, contemporary realism")}</div>
        {sel("Purpose of Acquisition", "purpose", "art-purpose", ART_PURPOSES, true)}
        {form.purpose === "OTHER" && field("Please specify the purpose", "purpose_other", "art-purpose-other", "e.g. Charitable donation", true)}
        <div className="sm:col-span-2">{field("Preferred Artists", "artists", "art-artists", "One or several artists, or let us know if you have no preference")}</div>
        {sel("Acquisition Frequency", "frequency", "art-frequency", ART_FREQUENCIES)}
        {sel("Preferred Region for Sourcing / Viewing", "region", "art-region", ART_REGIONS)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Logistics & Discretion")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Delivery / Shipping Method", "delivery_method", "art-delivery", ART_DELIVERY_METHODS)}
        {field("Collection / Acquisition Location", "collection_location", "art-collection-location", "e.g. A specific gallery in Paris")}
        {sel("Confidentiality Preferences", "confidentiality", "art-confidentiality", ART_CONFIDENTIALITY)}
        {sel("Art Advisor Required?", "advisor_required", "art-advisor", ["YES", "NO"])}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        {field("Acquisition Budget Range", "budget_range", "art-budget", "e.g. GBP 50,000 – 250,000")}
      </div>
      <div className="mt-6">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Special Requests")}</label>
        <textarea data-testid="art-special-requests" rows={3} value={form.special_requests} onChange={set("special_requests")} placeholder={tr("Provenance requirements, viewing arrangements, framing, insurance…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_ART = { art_type: "", period_style: "", purpose: "", purpose_other: "", artists: "", frequency: "", region: "", delivery_method: "", collection_location: "", confidentiality: "", advisor_required: "", budget_range: "", special_requests: "" };

function ArtRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_ART);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/art-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your art & collectibles requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="art-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Art & Collectibles Access Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can begin sourcing for you. Your general location, party size and overall budget are already noted from your request.")}</p>
      <ArtFields form={form} setForm={setForm} />
      <button
        data-testid="art-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Art & Collectibles Requirements")}
      </button>
      {error && <p data-testid="art-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const LOUNGE_PURPOSES = ["BUSINESS", "LEISURE", "BOTH"];
const LOUNGE_FREQUENCIES = ["FREQUENT", "OCCASIONAL", "MONTHLY", "WEEKLY", "DAILY"];
const YES_NO = ["YES", "NO"];

function LoungeFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "transfer_vehicle" && e.target.value === "NO") next.requested_vehicle = "";
    setForm(next);
  };
  const togglePurpose = (p) =>
    setForm({ ...form, purpose: form.purpose.includes(p) ? form.purpose.filter((x) => x !== p) : [...form.purpose, p] });

  const field = (label, key, testId, placeholder, required = false, type = "text") => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type === "date" ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Travel")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        <div className="sm:col-span-2">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-3">{tr("Purpose of Travel")}</label>
          <div className="flex flex-wrap gap-2.5" data-testid="lounge-purpose-options">
            {LOUNGE_PURPOSES.map((p) => (
              <button
                type="button"
                key={p}
                data-testid={`lounge-purpose-${p.toLowerCase()}`}
                onClick={() => togglePurpose(p)}
                className={`border text-[0.6rem] uppercase tracking-[0.18em] px-3.5 py-2.5 transition-colors duration-300 ${
                  form.purpose.includes(p) ? "border-gold/60 bg-gold/10 text-gold" : "border-white/15 text-white/60 hover:border-gold/40 hover:text-white/90"
                }`}
              >
                {tr(p)}
              </button>
            ))}
          </div>
        </div>
        {sel("Travel Frequency", "frequency", "lounge-frequency", LOUNGE_FREQUENCIES)}
        {field("Travel Date", "travel_date", "lounge-travel-date", "", false, "date")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Airport / Terminal Services")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Meet & Greet Required?", "meet_greet", "lounge-meet-greet", YES_NO)}
        {sel("Baggage Assistance Required?", "baggage_assistance", "lounge-baggage", YES_NO)}
        <div className="sm:col-span-2">{field("Preferred Lounges or Operators", "preferred_lounges", "lounge-preferred", "e.g. PS LAX, The Windsor Suite Heathrow, Falcona")}</div>
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Ground Transportation")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Transfer Vehicle Required?", "transfer_vehicle", "lounge-transfer", YES_NO)}
        {form.transfer_vehicle === "YES" && field("Requested Vehicle", "requested_vehicle", "lounge-vehicle", "e.g. Range Rover Autobiography, Mercedes S-Class", true)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Delux Concierge Co-ordination")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Trip Co-ordination Required?", "trip_coordination", "lounge-coordination", YES_NO)}
      </div>
      <div className="mt-2">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Requests")}</label>
        <textarea data-testid="lounge-additional" rows={3} value={form.additional_requests} onChange={set("additional_requests")} placeholder={tr("Anything else we should know…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_LOUNGE = { purpose: [], frequency: "", travel_date: "", transfer_vehicle: "", requested_vehicle: "", meet_greet: "", baggage_assistance: "", preferred_lounges: "", trip_coordination: "", additional_requests: "" };

function LoungeRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_LOUNGE);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/lounge-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your lounge & private terminal requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="lounge-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Global Lounge / Private Terminal Access Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can arrange everything seamlessly. Your general location, party size and overall budget are already noted from your request.")}</p>
      <LoungeFields form={form} setForm={setForm} />
      <button
        data-testid="lounge-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Requirements")}
      </button>
      {error && <p data-testid="lounge-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}


const CLUB_TYPES = ["PRIVATE MEMBERS", "YACHT CLUB", "COUNTRY CLUB", "GOLF CLUB", "TENNIS CLUB", "BUSINESS LOUNGE", "OTHER"];

function ClubFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "club_type" && e.target.value !== "OTHER") next.club_type_other = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false) => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className="input-lux" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Club Access")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        <div className="relative">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Type of Club Access")}</label>
          <select data-testid="club-type" required value={form.club_type} onChange={set("club_type")} className="input-lux appearance-none pr-8 cursor-pointer">
            <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
            {CLUB_TYPES.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
          </select>
          <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
        </div>
        {form.club_type === "OTHER" && field("Please specify the club type", "club_type_other", "club-type-other", "e.g. Polo club, beach club", true)}
        {field("Preferred Location / City", "location_city", "club-location", "e.g. Mayfair, London or Dubai")}
        {field("Name of Club (optional)", "club_name", "club-name", "If you have a particular club in mind")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Purpose")}</p>
      <div className="grid grid-cols-1 gap-y-6 mb-8">
        {field("Purpose of Visit", "purpose", "club-purpose", "e.g. Business meeting, networking, dining, private event")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="mt-2">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Special Requests")}</label>
        <textarea data-testid="club-special-requests" rows={3} value={form.special_requests} onChange={set("special_requests")} placeholder={tr("Guest list, dining reservations, reciprocal clubs, timings…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_CLUB = { club_type: "", club_type_other: "", location_city: "", club_name: "", purpose: "", special_requests: "" };

function ClubRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_CLUB);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/club-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your club access requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="club-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Elite Social Club Access Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can arrange the right access for you. Your general location, party size and overall budget are already noted from your request.")}</p>
      <ClubFields form={form} setForm={setForm} />
      <button
        data-testid="club-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Club Access Requirements")}
      </button>
      {error && <p data-testid="club-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

function DiningFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "occasion" && e.target.value === "NO") next.occasion_detail = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false, type = "text") => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type.startsWith("date") ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Restaurant & Reservation")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Would you like us to recommend a restaurant?", "recommendations", "dining-recommendations", YES_NO, true)}
        {field("Date & Time of Reservation", "reservation_datetime", "dining-datetime", "", false, "datetime-local")}
        {field("Preferred Location / Area", "location_area", "dining-location", "e.g. Mayfair, London or Central Paris")}
        {field(
          form.recommendations === "NO" ? tr("Name of Restaurant") : tr("Name of Restaurant (optional)"),
          "restaurant_name", "dining-restaurant",
          form.recommendations === "NO" ? tr("The restaurant you have in mind") : tr("If you already have somewhere in mind"),
          form.recommendations === "NO"
        )}
        {field("Number of Diners", "number_of_diners", "dining-diners", "e.g. 4")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Dining Preferences")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Allergies / Dietary Requirements", "allergies", "dining-allergies", "e.g. Nut allergy, vegetarian, halal")}
        {field("Dining Style", "dining_style", "dining-style", "e.g. Tasting menu, omakase, classic French")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Occasion")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Is There an Occasion?", "occasion", "dining-occasion", YES_NO)}
        {form.occasion === "YES" && field("Tell us about the occasion", "occasion_detail", "dining-occasion-detail", "e.g. Anniversary dinner", true)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="mt-2">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Special Requests")}</label>
        <textarea data-testid="dining-special-requests" rows={3} value={form.special_requests} onChange={set("special_requests")} placeholder={tr("Private dining room, window table, sommelier, celebration arrangements…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_DINING = { recommendations: "", reservation_datetime: "", location_area: "", restaurant_name: "", number_of_diners: "", allergies: "", dining_style: "", occasion: "", occasion_detail: "", special_requests: "" };

function DiningRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_DINING);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/dining-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your dining requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="dining-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Fine Dining Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can secure the perfect table. Your general location, party size and overall budget are already noted from your request.")}</p>
      <DiningFields form={form} setForm={setForm} />
      <button
        data-testid="dining-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Dining Requirements")}
      </button>
      {error && <p data-testid="dining-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const JEWELLERY_PURPOSES = ["PERSONAL", "GIFT", "ENGAGEMENT", "ANNIVERSARY", "INVESTMENT", "OTHER"];
const JEWELLERY_DELIVERY_METHODS = ["IN-PERSON COLLECTION", "DELIVERY", "SENT TO STORAGE"];

function JewelleryFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "occasion_purpose" && e.target.value !== "OTHER") next.occasion_other = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false) => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className="input-lux" />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Jewellery Requirement")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        <div className="sm:col-span-2">{field("Type of Jewellery Requested", "jewellery_type", "jewellery-type", "e.g. Diamond ring, tennis bracelet, bespoke commission", true)}</div>
        <div className="sm:col-span-2">{field("Preferred Brands / Designers", "brands_designers", "jewellery-brands", "e.g. Graff, Cartier, a particular designer — or leave blank for us to source")}</div>
        <div className="sm:col-span-2">{field("Preferred Jewellery Style / Specifications", "style_specifications", "jewellery-style", "e.g. Vintage, coloured gemstones, platinum, ring size…")}</div>
        {sel("Occasion or Purpose", "occasion_purpose", "jewellery-occasion", JEWELLERY_PURPOSES, true)}
        {form.occasion_purpose === "OTHER" && field("Please specify the occasion or purpose", "occasion_other", "jewellery-occasion-other", "e.g. Milestone birthday", true)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Budget")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Budget Range", "budget_range", "jewellery-budget", "e.g. GBP 25,000 – 75,000")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Delivery")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Preferred Delivery Method", "delivery_method", "jewellery-delivery", JEWELLERY_DELIVERY_METHODS)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="mt-2">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Notes / Special Requirements")}</label>
        <textarea data-testid="jewellery-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Certification, insurance, presentation, timings…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_JEWELLERY = { jewellery_type: "", brands_designers: "", occasion_purpose: "", occasion_other: "", style_specifications: "", budget_range: "", delivery_method: "", notes: "" };

function JewelleryRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_JEWELLERY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/jewellery-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your jewellery sourcing requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="jewellery-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Jewellery Sourcing Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can begin sourcing on your behalf. Your general location, party size and overall budget are already noted from your request.")}</p>
      <JewelleryFields form={form} setForm={setForm} />
      <button
        data-testid="jewellery-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Jewellery Requirements")}
      </button>
      {error && <p data-testid="jewellery-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const TRANSPORT_METHODS = ["AIR", "SEA", "ROAD", "EXPRESS / COURIER", "NO PREFERENCE"];
const SECURITY_LEVELS = ["LOW", "MEDIUM", "HIGH-VALUE SECURITY", "ULTRA HIGH VALUE SECURITY"];

function ImportExportFields({ form, setForm, docs, setDocs }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const field = (label, key, testId, placeholder, required = false) => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className="input-lux" />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Goods")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        <div className="sm:col-span-2">{field("Type of Good Being Transported", "goods_type", "ie-goods-type", "e.g. Fine art, classic car, personal effects", true)}</div>
        <div className="sm:col-span-2">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Description of Item")}</label>
          <textarea data-testid="ie-description" rows={2} value={form.description} onChange={set("description")} placeholder={tr("Dimensions, weight, fragility, materials…")} className="input-lux resize-none" />
        </div>
        {field("Quantity / Number of Goods", "quantity", "ie-quantity", "e.g. 3")}
        {field("Declared Value of Goods", "declared_value", "ie-declared-value", "e.g. GBP 250,000")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Logistics")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Collection / Origin Location", "origin_location", "ie-origin", "e.g. Geneva vault")}
        {field("Delivery Destination", "delivery_destination", "ie-destination", "e.g. Mayfair, London")}
        {sel("Preferred Transport Method", "transport_method", "ie-transport", TRANSPORT_METHODS)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Documentation")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Do you have the import & export documentation?", "documentation", "ie-documentation", YES_NO, true)}
        {form.documentation === "YES" && (
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Upload Documents (optional)")}</label>
            <input
              data-testid="ie-documents"
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
              onChange={(e) => setDocs(Array.from(e.target.files || []).slice(0, 5))}
              className="block w-full text-xs text-white/60 file:bg-transparent file:border file:border-gold/40 file:text-gold file:text-[0.6rem] file:uppercase file:tracking-[0.2em] file:px-4 file:py-2.5 file:mr-4 file:cursor-pointer hover:file:border-gold transition-colors"
            />
            {docs.length > 0 && <p className="text-white/40 text-xs mt-2 font-light" data-testid="ie-documents-list">{docs.map((d) => d.name).join(", ")}</p>}
          </div>
        )}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Security")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Required Security Level", "security_level", "ie-security", SECURITY_LEVELS)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="mt-2">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Notes / Special Requirements")}</label>
        <textarea data-testid="ie-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Customs considerations, timings, handling instructions…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_IMPORT_EXPORT = { goods_type: "", description: "", quantity: "", declared_value: "", origin_location: "", delivery_destination: "", transport_method: "", documentation: "", security_level: "", notes: "" };

async function uploadImportExportDocs(requestId, docs) {
  const fd = new FormData();
  docs.forEach((d) => fd.append("files", d));
  await axios.post(`${API}/portal/service-requests/${requestId}/import-export-documents`, fd, {
    headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
  });
}

function ImportExportRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_IMPORT_EXPORT);
  const [docs, setDocs] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/import-export-requirements`, form, { headers: authHeader() });
      if (docs.length > 0) {
        await uploadImportExportDocs(request.id, docs);
      }
      toast.success(tr("Your import & export requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="import-export-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Import & Export Management Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can manage the movement for you. Your general location, party size and overall budget are already noted from your request.")}</p>
      <ImportExportFields form={form} setForm={setForm} docs={docs} setDocs={setDocs} />
      <button
        data-testid="import-export-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Import & Export Requirements")}
      </button>
      {error && <p data-testid="import-export-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const PROPERTY_TYPES = ["APARTMENT", "VILLA", "HOUSE", "ESTATE", "PENTHOUSE", "COMMERCIAL PROPERTY", "YACHT", "OTHER"];
const INSTALLATION_TYPES = ["HOME AUTOMATION", "HOME CINEMA", "AUDIO-VISUAL INTEGRATION", "CLIMATE CONTROL", "FULL SMART HOME INSTALLATION", "HOME SECURITY SYSTEMS", "OTHER"];

function HomeAutomationFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "property_type" && e.target.value !== "OTHER") next.property_type_other = "";
    if (k === "installation_type" && e.target.value !== "OTHER") next.installation_type_other = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false) => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className="input-lux" />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Property")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Property Type", "property_type", "ha-property-type", PROPERTY_TYPES, true)}
        {form.property_type === "OTHER" && field("Please specify the property type", "property_type_other", "ha-property-type-other", "e.g. Chateau, private island residence", true)}
        {field("Estimated Property Size", "property_size", "ha-property-size", "e.g. 12,000 sq ft / 8 bedrooms")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Project Requirements")}</p>
      <div className="grid grid-cols-1 gap-y-6 mb-8">
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Scope of Work")}</label>
          <textarea data-testid="ha-scope" rows={3} value={form.scope_of_work} onChange={set("scope_of_work")} placeholder={tr("e.g. Full villa automation including lighting, climate control, security, entertainment and automated blinds")} className="input-lux resize-none" />
        </div>
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Installation")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Type of Installation Required", "installation_type", "ha-installation-type", INSTALLATION_TYPES, true)}
        {form.installation_type === "OTHER" && field("Please specify the installation", "installation_type_other", "ha-installation-type-other", "e.g. Estate network & infrastructure", true)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Budget")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Budget", "budget", "ha-budget", "e.g. GBP 100,000 – 250,000")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="mt-2">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Special Requests")}</label>
        <textarea data-testid="ha-special-requests" rows={3} value={form.special_requests} onChange={set("special_requests")} placeholder={tr("Existing systems to integrate with, preferred installers, timings, access considerations…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_HOME_AUTOMATION = { property_type: "", property_type_other: "", property_size: "", scope_of_work: "", installation_type: "", installation_type_other: "", budget: "", special_requests: "" };

function HomeAutomationRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_HOME_AUTOMATION);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/home-automation-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your home automation requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="home-automation-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Home Automation & Estate Technology Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can scope the project for you. Your general location, party size and overall budget are already noted from your request.")}</p>
      <HomeAutomationFields form={form} setForm={setForm} />
      <button
        data-testid="home-automation-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Home Automation Requirements")}
      </button>
      {error && <p data-testid="home-automation-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const EDUCATION_TYPES = ["PRIVATE TUTORING", "PRIVATE SCHOOL", "UNIVERSITY", "EXAM PREP", "LANGUAGE TUITION", "HOME SCHOOLING", "ONLINE PROGRAMS", "SUMMER SCHOOLS"];
const EDUCATION_LEVELS = ["PRIMARY", "SECONDARY", "GCSE", "A-LEVEL", "IB", "UNIVERSITY", "PROFESSIONAL", "OTHER"];

function EducationFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "education_level" && e.target.value !== "OTHER") next.education_level_other = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false, type = "text") => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type === "date" ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Education Requirement")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Education Type", "education_type", "edu-type", EDUCATION_TYPES, true)}
        {sel("Education Level", "education_level", "edu-level", EDUCATION_LEVELS, true)}
        {form.education_level === "OTHER" && field("Please specify the education level", "education_level_other", "edu-level-other", "e.g. Baccalauréat, AP", true)}
        {field("Student Age", "student_age", "edu-student-age", "e.g. 14")}
        <div className="sm:col-span-2">{field("Preferred Subjects", "subjects", "edu-subjects", "e.g. Mathematics, Physics, French")}</div>
        <div className="sm:col-span-2">{field("Preferred Education Institutes", "institutes", "edu-institutes", "Any preferred schools, universities or providers")}</div>
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Tutoring Requirements")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Preferred Tutor Gender (optional)", "tutor_gender", "edu-tutor-gender", "e.g. Female, no preference")}
        {field("Preferred Study Location", "study_location", "edu-study-location", "e.g. Residence, online, tutor's premises")}
        {field("Preferred Start Date", "start_date", "edu-start-date", "", false, "date")}
        {field("Tutoring Frequency / Schedule", "frequency_schedule", "edu-frequency", "e.g. Twice per week, after school")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Budget")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Budget", "budget", "edu-budget", "e.g. GBP 500 – 1,000 per month")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="mt-2">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Service Notes")}</label>
        <textarea data-testid="edu-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Learning objectives, exam boards, special educational needs, timings…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_EDUCATION = { education_type: "", education_level: "", education_level_other: "", student_age: "", tutor_gender: "", subjects: "", study_location: "", start_date: "", frequency_schedule: "", institutes: "", budget: "", notes: "" };

function EducationRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_EDUCATION);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/education-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your education requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="education-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Private Education & Tutoring Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can match the right educational support. Your general location, party size and overall budget are already noted from your request.")}</p>
      <EducationFields form={form} setForm={setForm} />
      <button
        data-testid="education-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Education Requirements")}
      </button>
      {error && <p data-testid="education-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const CORP_TRAVEL_PURPOSES = ["BUSINESS MEETING", "CLIENT VISIT", "CONFERENCE / EVENT", "EXECUTIVE TRAVEL", "SITE VISIT", "INCENTIVE TRAVEL", "OTHER"];
const CORP_TRAVEL_TYPES = ["PRIVATE", "LUXURY COMMERCIAL", "HELICOPTER", "OTHER"];

function CorpFlightFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "travel_purpose" && e.target.value !== "OTHER") next.travel_purpose_other = "";
    if (k === "travel_type" && e.target.value !== "OTHER") next.travel_type_other = "";
    if (k === "return_trip" && e.target.value === "NO") {
      next.return_departure_airport = "";
      next.return_arrival_airport = "";
      next.return_datetime = "";
    }
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false, type = "text") => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type.startsWith("date") ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Corporate Travel")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Corporate Travel Purpose", "travel_purpose", "cf-purpose", CORP_TRAVEL_PURPOSES, true)}
        {form.travel_purpose === "OTHER" && field("Please specify the purpose", "travel_purpose_other", "cf-purpose-other", "e.g. Board roadshow", true)}
        {sel("Type of Travel", "travel_type", "cf-travel-type", CORP_TRAVEL_TYPES, true)}
        {form.travel_type === "OTHER" && field("Please specify the type of travel", "travel_type_other", "cf-travel-type-other", "e.g. Group charter", true)}
        {field("Departure Airport", "departure_airport", "cf-departure", "e.g. London Farnborough", true)}
        {field("Arrival Airport", "arrival_airport", "cf-arrival", "e.g. Geneva", true)}
        {field("Date / Time of Departure", "departure_datetime", "cf-departure-datetime", "", false, "datetime-local")}
        {field("Number of Passengers", "passengers", "cf-passengers", "e.g. 6")}
        {field("Number of Luggage", "luggage", "cf-luggage", "e.g. 10")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Return Journey")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Return Trip Required?", "return_trip", "cf-return-trip", YES_NO, true)}
        {form.return_trip === "YES" && (
          <>
            {field("Return Departure Airport", "return_departure_airport", "cf-return-departure", "e.g. Geneva", true)}
            {field("Return Arrival Airport", "return_arrival_airport", "cf-return-arrival", "e.g. London Farnborough", true)}
            {field("Date / Time of Return", "return_datetime", "cf-return-datetime", "", false, "datetime-local")}
          </>
        )}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Ground Transportation")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Ground Transportation Needed?", "ground_transport", "cf-ground", YES_NO)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Onboard Requirements")}</p>
      <div className="grid grid-cols-1 gap-y-6 mb-8">
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Catering / Onboard Requests")}</label>
          <textarea data-testid="cf-catering" rows={2} value={form.catering} onChange={set("catering")} placeholder={tr("Catering preferences, onboard amenities, working requirements…")} className="input-lux resize-none" />
        </div>
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Passengers")}</p>
      <div className="grid grid-cols-1 gap-y-6 mb-8">
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Passenger Details")}</label>
          <textarea data-testid="cf-passenger-details" rows={2} value={form.passenger_details} onChange={set("passenger_details")} placeholder={tr("Names / roles of passengers, or a manifest reference — no passport details needed at this stage")} className="input-lux resize-none" />
        </div>
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="mt-2">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Service Notes")}</label>
        <textarea data-testid="cf-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Anything else we should know…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_CORP_FLIGHT = { travel_purpose: "", travel_purpose_other: "", travel_type: "", travel_type_other: "", departure_airport: "", arrival_airport: "", departure_datetime: "", passengers: "", luggage: "", return_trip: "", return_departure_airport: "", return_arrival_airport: "", return_datetime: "", ground_transport: "", catering: "", passenger_details: "", notes: "" };

function CorpFlightRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_CORP_FLIGHT);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/corp-flight-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your flight coordination requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="corp-flight-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Corporate Flight Coordination Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can coordinate the journey. Your general location, party size and overall budget are already noted from your request.")}</p>
      <CorpFlightFields form={form} setForm={setForm} />
      <button
        data-testid="corp-flight-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Flight Coordination Requirements")}
      </button>
      {error && <p data-testid="corp-flight-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const RETREAT_TYPES = ["EXECUTIVE RETREAT", "TEAM BUILDING", "INCENTIVE TRIP", "LEADERSHIP RETREAT", "CORPORATE CELEBRATION", "STRATEGY / PLANNING RETREAT", "WELLNESS RETREAT", "OTHER"];
const ACCOMMODATION_PREFS = ["LUXURY HOTEL", "PRIVATE VILLA / ESTATE", "RESORT", "BOUTIQUE PROPERTY", "EXCLUSIVE USE PROPERTY", "OTHER"];

function RetreatFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "retreat_type" && e.target.value !== "OTHER") next.retreat_type_other = "";
    if (k === "accommodation_required" && e.target.value === "NO") {
      next.accommodation_preference = "";
      next.accommodation_other = "";
    }
    if (k === "accommodation_preference" && e.target.value !== "OTHER") next.accommodation_other = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false, type = "text") => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type === "date" ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Retreat Overview")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        <div className="sm:col-span-2">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Retreat Objective")}</label>
          <textarea data-testid="retreat-objective" rows={2} value={form.objective} onChange={set("objective")} placeholder={tr("e.g. Leadership alignment and strategic planning following annual results")} className="input-lux resize-none" />
        </div>
        {sel("Retreat Type", "retreat_type", "retreat-type", RETREAT_TYPES, true)}
        {form.retreat_type === "OTHER" && field("Please specify the retreat type", "retreat_type_other", "retreat-type-other", "e.g. Founder offsite", true)}
        {field("Destination", "destination", "retreat-destination", "e.g. Tuscany — or tell us if you'd like recommendations")}
        {field("Number of Attendees", "attendees", "retreat-attendees", "e.g. 25")}
        {field("Preferred Start Date", "date_from", "retreat-date-from", "", false, "date")}
        {field("Preferred End Date", "date_to", "retreat-date-to", "", false, "date")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Accommodation")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Accommodation Required?", "accommodation_required", "retreat-accommodation", YES_NO, true)}
        {form.accommodation_required === "YES" && (
          <>
            {sel("Accommodation Preference", "accommodation_preference", "retreat-accommodation-pref", ACCOMMODATION_PREFS)}
            {form.accommodation_preference === "OTHER" && field("Please specify the accommodation", "accommodation_other", "retreat-accommodation-other", "e.g. Private chateau, exclusive hire", true)}
          </>
        )}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Corporate Requirements")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Meeting / Conference Facilities Required?", "meeting_facilities", "retreat-facilities", YES_NO)}
        {sel("Transportation Required?", "transportation", "retreat-transportation", YES_NO)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Budget")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Budget", "budget", "retreat-budget", "e.g. GBP 50,000 – 120,000")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="grid grid-cols-1 gap-y-6 mb-6">
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Dietary / Special Requirements")}</label>
          <textarea data-testid="retreat-dietary" rows={2} value={form.dietary} onChange={set("dietary")} placeholder={tr("Dietary needs, accessibility requirements, participant considerations…")} className="input-lux resize-none" />
        </div>
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Service Notes")}</label>
          <textarea data-testid="retreat-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Anything else we should know…")} className="input-lux resize-none" />
        </div>
      </div>
    </>
  );
}

const EMPTY_RETREAT = { objective: "", retreat_type: "", retreat_type_other: "", destination: "", date_from: "", date_to: "", attendees: "", accommodation_required: "", accommodation_preference: "", accommodation_other: "", meeting_facilities: "", transportation: "", budget: "", dietary: "", notes: "" };

function RetreatRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_RETREAT);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/retreat-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your retreat requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="retreat-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Tailored Corporate Retreats Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can design the retreat around your objectives. Your general location, party size and overall budget are already noted from your request.")}</p>
      <RetreatFields form={form} setForm={setForm} />
      <button
        data-testid="retreat-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Retreat Requirements")}
      </button>
      {error && <p data-testid="retreat-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const RELOCATION_TYPES = ["CORPORATE EMPLOYEE RELOCATION", "OFFICE RELOCATION", "EXECUTIVE RELOCATION", "INTERNATIONAL RELOCATION", "DOMESTIC RELOCATION", "OTHER"];

function RelocationFields({ form, setForm, docs, setDocs }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "relocation_type" && e.target.value !== "OTHER") next.relocation_type_other = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false, type = "text") => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type === "date" ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Relocation Overview")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Type of Relocation", "relocation_type", "rel-type", RELOCATION_TYPES, true)}
        {form.relocation_type === "OTHER" && field("Please specify the type of relocation", "relocation_type_other", "rel-type-other", "e.g. Factory equipment move", true)}
        {field("Moving Date", "moving_date", "rel-moving-date", "", false, "date")}
        <div className="sm:col-span-2">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Required Services")}</label>
          <textarea data-testid="rel-services" rows={2} value={form.required_services} onChange={set("required_services")} placeholder={tr("e.g. Full packing and removal, furniture dismantling/reassembly, international shipping, temporary storage, unpacking, property setup, employee relocation support")} className="input-lux resize-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Relocation Scope / Description")}</label>
          <textarea data-testid="rel-scope" rows={2} value={form.scope} onChange={set("scope")} placeholder={tr("e.g. Relocation of a senior executive and family from London to Geneva, including household furniture, personal effects and two vehicles")} className="input-lux resize-none" />
        </div>
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Locations")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Current Address", "current_address", "rel-current-address", "Where the goods will be collected from")}
        {field("New Address", "new_address", "rel-new-address", "The destination property")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Logistics")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Packing Required?", "packing", "rel-packing", YES_NO)}
        {sel("Storage Required?", "storage", "rel-storage", YES_NO)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Inventory")}</p>
      <div className="grid grid-cols-1 gap-y-6 mb-8">
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Item Inventory (optional)")}</label>
          <input
            data-testid="rel-inventory"
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.webp"
            onChange={(e) => setDocs(Array.from(e.target.files || []).slice(0, 5))}
            className="block w-full text-xs text-white/60 file:bg-transparent file:border file:border-gold/40 file:text-gold file:text-[0.6rem] file:uppercase file:tracking-[0.2em] file:px-4 file:py-2.5 file:mr-4 file:cursor-pointer hover:file:border-gold transition-colors"
          />
          {docs.length > 0 && <p className="text-white/40 text-xs mt-2 font-light" data-testid="rel-inventory-list">{docs.map((d) => d.name).join(", ")}</p>}
        </div>
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="mt-2">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Service Notes")}</label>
        <textarea data-testid="rel-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Access considerations, timings, insurance, anything else…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_RELOCATION = { relocation_type: "", relocation_type_other: "", required_services: "", moving_date: "", scope: "", current_address: "", new_address: "", packing: "", storage: "", notes: "" };

async function uploadRelocationInventory(requestId, docs) {
  const fd = new FormData();
  docs.forEach((d) => fd.append("files", d));
  await axios.post(`${API}/portal/service-requests/${requestId}/relocation-inventory`, fd, {
    headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
  });
}

function RelocationRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_RELOCATION);
  const [docs, setDocs] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/relocation-requirements`, form, { headers: authHeader() });
      if (docs.length > 0) {
        await uploadRelocationInventory(request.id, docs);
      }
      toast.success(tr("Your relocation requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="relocation-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Relocations / Removals Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can manage the move for you. Your general location, party size and overall budget are already noted from your request.")}</p>
      <RelocationFields form={form} setForm={setForm} docs={docs} setDocs={setDocs} />
      <button
        data-testid="relocation-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Relocation Requirements")}
      </button>
      {error && <p data-testid="relocation-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const NETWORKING_OBJECTIVES = ["CLIENT NETWORKING", "INVESTOR RELATIONS", "BUSINESS DEVELOPMENT", "INDUSTRY NETWORKING", "BRAND EXPOSURE", "EXECUTIVE NETWORKING", "RELATIONSHIP BUILDING"];
const NETWORKING_FORMATS = ["NETWORKING RECEPTION", "PRIVATE DINNER", "COCKTAIL EVENT", "CONFERENCE", "INDUSTRY EVENT", "PRIVATE MEMBERS EVENT", "CORPORATE GATHERING", "OTHER"];
const NETWORKING_REQUIREMENTS = ["EVENT PLANNING", "ACCESS TO EXISTING EVENT", "BOTH"];
const NETWORKING_INDUSTRIES = ["FINANCE", "TECHNOLOGY", "REAL ESTATE", "LUXURY", "ART", "FASHION", "PRIVATE EQUITY", "HOSPITALITY", "AVIATION", "YACHTING", "OTHER"];

function NetworkingFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "event_format" && e.target.value !== "OTHER") next.event_format_other = "";
    if (k === "requirements" && e.target.value === "EVENT PLANNING") next.existing_event_type = "";
    if (k === "industry" && e.target.value !== "OTHER") next.industry_other = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false, type = "text") => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type === "date" ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Event Overview")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Event Objective / Purpose", "objective", "nw-objective", NETWORKING_OBJECTIVES, true)}
        {sel("Event Format", "event_format", "nw-format", NETWORKING_FORMATS, true)}
        {form.event_format === "OTHER" && field("Please specify the event format", "event_format_other", "nw-format-other", "e.g. Investor breakfast", true)}
        {field("Preferred Event Date", "event_date", "nw-date", "", false, "date")}
        {field("Number of Attendees", "attendees", "nw-attendees", "e.g. 40")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Access & Networking")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {sel("Event Requirements", "requirements", "nw-requirements", NETWORKING_REQUIREMENTS, true)}
        {(form.requirements === "ACCESS TO EXISTING EVENT" || form.requirements === "BOTH") &&
          field("Existing Event Type", "existing_event_type", "nw-existing-event", "e.g. Industry summit, trade fair, members club event", true)}
        {sel("Preferred Industry / Network", "industry", "nw-industry", NETWORKING_INDUSTRIES)}
        {form.industry === "OTHER" && field("Please specify the industry or network", "industry_other", "nw-industry-other", "e.g. Sovereign wealth", true)}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Budget")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Event Budget", "budget", "nw-budget", "e.g. GBP 25,000 – 75,000")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="mt-2">
        <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Service Notes")}</label>
        <textarea data-testid="nw-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Guest profile, hosting preferences, timings, anything else…")} className="input-lux resize-none" />
      </div>
    </>
  );
}

const EMPTY_NETWORKING = { objective: "", event_format: "", event_format_other: "", event_date: "", attendees: "", requirements: "", existing_event_type: "", industry: "", industry_other: "", budget: "", notes: "" };

function NetworkingRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_NETWORKING);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/networking-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your networking event requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="networking-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Networking Event Planning & Access Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can plan or secure the right access for you. Your general location, party size and overall budget are already noted from your request.")}</p>
      <NetworkingFields form={form} setForm={setForm} />
      <button
        data-testid="networking-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Networking Requirements")}
      </button>
      {error && <p data-testid="networking-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const INCENTIVE_PURPOSES = ["EMPLOYEE REWARD", "EMPLOYEE RECOGNITION", "CLIENT APPRECIATION", "PARTNER APPRECIATION", "SALES INCENTIVE", "PERFORMANCE REWARD", "EXECUTIVE INCENTIVE", "TEAM BUILDING", "OTHER"];
const INCENTIVE_FREQUENCIES = ["ONE-OFF", "MONTHLY", "QUARTERLY", "ANNUALLY"];
const INCENTIVE_ACCOMMODATION = ["LUXURY HOTEL", "RESORT", "PRIVATE VILLA / ESTATE", "BOUTIQUE PROPERTY", "EXCLUSIVE USE PROPERTY", "OTHER"];

function IncentiveFields({ form, setForm }) {
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "purpose" && e.target.value !== "OTHER") next.purpose_other = "";
    if (k === "accommodation_required" && e.target.value === "NO") {
      next.accommodation = "";
      next.accommodation_other = "";
    }
    if (k === "accommodation" && e.target.value !== "OTHER") next.accommodation_other = "";
    setForm(next);
  };

  const field = (label, key, testId, placeholder, required = false, type = "text") => (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <input data-testid={testId} required={required} type={type} value={form[key]} onChange={set(key)} placeholder={placeholder ? tr(placeholder) : placeholder} className={`input-lux${type === "date" ? " cursor-pointer [color-scheme:dark]" : ""}`} />
    </div>
  );

  const sel = (label, key, testId, options, required = false) => (
    <div className="relative">
      <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr(label)}</label>
      <select data-testid={testId} required={required} value={form[key]} onChange={set(key)} className="input-lux appearance-none pr-8 cursor-pointer">
        <option value="" className="bg-[#0A0A0B]">{tr("Select…")}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>)}
      </select>
      <Plus strokeWidth={1.5} size={13} className="absolute right-0 top-9 pointer-events-none text-gold/70 rotate-45" />
    </div>
  );

  return (
    <>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Incentive Overview")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Incentive Type", "incentive_type", "incentive-type", "e.g. Incentive trip, experience package, recognition award", true)}
        {sel("Incentive Purpose", "purpose", "incentive-purpose", INCENTIVE_PURPOSES, true)}
        {form.purpose === "OTHER" && field("Please specify the purpose", "purpose_other", "incentive-purpose-other", "e.g. Channel partner reward", true)}
        {field("Number of Participants", "participants", "incentive-participants", "e.g. 30")}
        {sel("Frequency of Incentives", "frequency", "incentive-frequency", INCENTIVE_FREQUENCIES)}
        {field("Preferred Start Date", "date_from", "incentive-date-from", "", false, "date")}
        {field("Preferred End Date", "date_to", "incentive-date-to", "", false, "date")}
        {field("Duration", "duration", "incentive-duration", "e.g. 3 nights, 1 week")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Destination & Accommodation")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Preferred Destination", "destination", "incentive-destination", "e.g. Monaco — or tell us if you'd like recommendations")}
        {sel("Accommodation Required?", "accommodation_required", "incentive-accommodation", YES_NO, true)}
        {form.accommodation_required === "YES" && (
          <>
            {sel("Preferred Accommodation", "accommodation", "incentive-accommodation-pref", INCENTIVE_ACCOMMODATION)}
            {form.accommodation === "OTHER" && field("Please specify the accommodation", "accommodation_other", "incentive-accommodation-other", "e.g. Private chateau, exclusive hire", true)}
          </>
        )}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Experiences & Activities")}</p>
      <div className="grid grid-cols-1 gap-y-6 mb-8">
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Experiences / Activities Required")}</label>
          <textarea data-testid="incentive-experiences" rows={2} value={form.experiences} onChange={set("experiences")} placeholder={tr("e.g. Yacht charter, fine dining, golf, wellness, motorsport, cultural experiences, private events")} className="input-lux resize-none" />
        </div>
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Budget")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        {field("Budget Per Person", "budget_per_person", "incentive-budget", "e.g. GBP 2,500")}
      </div>
      <p className="text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-4">{tr("Additional Requirements")}</p>
      <div className="grid grid-cols-1 gap-y-6">
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Services")}</label>
          <textarea data-testid="incentive-additional-services" rows={2} value={form.additional_services} onChange={set("additional_services")} placeholder={tr("Anything else you'd like us to arrange as part of the incentive…")} className="input-lux resize-none" />
        </div>
        <div>
          <label className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Additional Service Notes")}</label>
          <textarea data-testid="incentive-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={tr("Anything else we should know…")} className="input-lux resize-none" />
        </div>
      </div>
    </>
  );
}

const EMPTY_INCENTIVE = { incentive_type: "", purpose: "", purpose_other: "", participants: "", frequency: "", date_from: "", date_to: "", duration: "", destination: "", accommodation_required: "", accommodation: "", accommodation_other: "", experiences: "", budget_per_person: "", additional_services: "", notes: "" };

function IncentiveRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_INCENTIVE);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/incentive-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your incentive requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="incentive-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Employee & Client Incentives Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can design the incentive around your goals. Your general location, party size and overall budget are already noted from your request.")}</p>
      <IncentiveFields form={form} setForm={setForm} />
      <button
        data-testid="incentive-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Incentive Requirements")}
      </button>
      {error && <p data-testid="incentive-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}













function DailyNeedsRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_DAILY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/daily-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="daily-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Daily Needs Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can begin sourcing the right people for your household. Your location, party size and budget are already noted from your request.")}</p>
      <DailyNeedsFields form={form} setForm={setForm} />
      <button
        data-testid="daily-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Daily Needs Requirements")}
      </button>
      {error && <p data-testid="daily-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

function WellnessRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_WELLNESS);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/wellness-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your wellness requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="wellness-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Health & Wellness Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can source the right practitioner for you. Your date, location, party size and budget are already noted from your request.")}</p>
      <WellnessFields form={form} setForm={setForm} />
      <button
        data-testid="wellness-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Wellness Requirements")}
      </button>
      {error && <p data-testid="wellness-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

function EventsRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_EVENTS);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/events-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your event requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="events-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Events & Experiences Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can begin curating your event. Your location, guest count and budget are already noted from your request.")}</p>
      <EventsFields form={form} setForm={setForm} />
      <button
        data-testid="events-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Event Requirements")}
      </button>
      {error && <p data-testid="events-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

function ChauffeurRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_CHAUFFEUR);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/chauffeur-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your chauffeur requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="chauffeur-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Chauffeur & Transfers Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can arrange your chauffeur. Your party size, budget and destination are already noted from your request.")}</p>
      <ChauffeurFields form={form} setForm={setForm} />
      <button
        data-testid="chauffeur-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Chauffeur Requirements")}
      </button>
      {error && <p data-testid="chauffeur-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

function SupercarRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_SUPERCAR);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/supercar-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your vehicle requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="supercar-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Super Car Hire Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can begin sourcing your vehicle. Your party size, budget and destination are already noted from your request.")}</p>
      <SupercarFields form={form} setForm={setForm} />
      <button
        data-testid="supercar-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Vehicle Requirements")}
      </button>
      {error && <p data-testid="supercar-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

function SecurityRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_SECURITY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/security-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your security requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="security-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Security Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can begin structuring your protection. Your date, location, party size and budget are already noted from your request.")}</p>
      <SecurityFields form={form} setForm={setForm} />
      <button
        data-testid="security-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Security Requirements")}
      </button>
      {error && <p data-testid="security-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

function JetRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_JET);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/jet-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your jet charter requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="jet-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Jet Charter Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can begin arranging your aircraft. Your party size, budget and destination are already noted from your request.")}</p>
      <JetFields form={form} setForm={setForm} />
      <button
        data-testid="jet-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Jet Requirements")}
      </button>
      {error && <p data-testid="jet-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

function YachtRequirementsForm({ request, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_YACHT);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.put(`${API}/portal/service-requests/${request.id}/yacht-requirements`, form, { headers: authHeader() });
      toast.success(tr("Your yacht charter requirements have been received"));
      onSubmitted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="yacht-requirements-form">
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-2">{tr("Yacht Charter Requirements")}</p>
      <p className="text-white/45 text-xs font-light mb-7 leading-relaxed">{tr("A few final details so we can begin sourcing your yacht. Your destination, party size and budget are already noted from your request.")}</p>
      <YachtFields form={form} setForm={setForm} />
      <button
        data-testid="yacht-submit"
        disabled={sending}
        className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
      >
        {sending ? tr("Submitting…") : tr("Submit Yacht Requirements")}
      </button>
      {error && <p data-testid="yacht-error" className="text-red-400/90 text-xs tracking-wide text-center mt-4">{error}</p>}
    </form>
  );
}

const requirementsPending = (r) =>
  (r.requested_service === "Luxury Travel" && !r.travel_requirements) ||
  (r.requested_service === "Yacht Charter" && !r.yacht_requirements) ||
  (r.requested_service === "Private Jet Charter" && !r.jet_requirements) ||
  (r.requested_service === "Security" && !r.security_requirements) ||
  (r.requested_service === "Supercar Hire" && !r.supercar_requirements) ||
  (r.requested_service === "Chauffeurs & Transfers" && !r.chauffeur_requirements) ||
  (r.requested_service === "Events & Experiences" && !r.events_requirements) ||
  (r.requested_service === "Health & Wellness" && !r.wellness_requirements) ||
  (r.requested_service === "Daily Needs" && !r.daily_requirements) ||
  (r.requested_service === "Personal Shopping & Sourcing" && !r.shopping_requirements) ||
  (r.requested_service === "Art & Collectibles Access" && !r.art_requirements) ||
  (r.requested_service === "Global Lounge / Private Terminal Access" && !r.lounge_requirements) ||
  (r.requested_service === "Elite Social Club Access" && !r.club_requirements) ||
  (r.requested_service === "Fine Dining" && !r.dining_requirements) ||
  (r.requested_service === "Jewellery Sourcing" && !r.jewellery_requirements) ||
  (r.requested_service === "Import & Export Management" && !r.import_export_requirements) ||
  (r.requested_service === "Home Automation & Estate Technology" && !r.home_automation_requirements) ||
  (r.requested_service === "Private Education & Tutoring" && !r.education_requirements) ||
  (r.requested_service === "Corporate Flight Coordination" && !r.corp_flight_requirements) ||
  (r.requested_service === "Tailored Corporate Retreats" && !r.retreat_requirements) ||
  (r.requested_service === "Relocations / Removals" && !r.relocation_requirements) ||
  (r.requested_service === "Networking Event Planning & Access" && !r.networking_requirements) ||
  (r.requested_service === "Employee & Client Incentives" && !r.incentive_requirements);


const CRM_STATUS_STYLES = {
  "RECEIVED": "text-white/70 border-white/20",
  "IN REVIEW": "text-gold/90 border-gold/40",
  "IN PROGRESS": "text-gold border-gold/60 bg-gold/5",
  "AWAITING CLIENT": "text-amber-300 border-amber-400/50 bg-amber-400/5",
  "OPTIONS READY": "text-champagne border-champagne/50",
  "PROPOSAL READY": "text-champagne border-champagne/50",
  "AWAITING APPROVAL": "text-amber-300 border-amber-400/50 bg-amber-400/5",
  "APPROVED": "text-emerald-300 border-emerald-400/40",
  "AWAITING PAYMENT": "text-amber-300 border-amber-400/50 bg-amber-400/5",
  "CONFIRMED": "text-emerald-300 border-emerald-400/40 bg-emerald-400/5",
  "COMPLETED": "text-white/50 border-white/25",
  "CANCELLED": "text-red-400/80 border-red-400/30",
};

const fmtSyncDate = (v) => {
  if (!v || typeof v !== "string") return v;
  const d = new Date(v.length === 10 ? `${v}T00:00:00` : v);
  return isNaN(d) ? v : d.toLocaleDateString(locale(), { day: "numeric", month: "long", year: "numeric" });
};

const fmtSyncBudget = (v) => (typeof v === "number" ? v.toLocaleString(locale()) : v);

const displayRequestTitle = (r) => {
  if (r.requested_service) {
    return tr("Service Request — {{service}}", { service: tr(r.requested_service) });
  }
  const value = r.enquiry_type || "Service Request";
  if (value.startsWith("Service Enquiry — ")) {
    return tr("Service Enquiry — {{service}}", { service: tr(value.slice(18)) });
  }
  if (value.startsWith("Request for Admission — ")) {
    return tr("Request for Admission — {{tier}} Membership", { tier: tr(value.slice(24).replace(" Membership", "")) });
  }
  return tr(value);
};

const displayStatus = (status, fallback = "In Review") => {
  const value = status || fallback;
  const translated = tr(value);
  if (translated !== value) return translated;
  const prefix = value.split(" - ")[0];
  return tr(prefix);
};

const displayValue = (value) => (typeof value === "string" ? tr(value) : value);

function ClientRequestSummary({ summary }) {
  if (!summary || !Object.values(summary).some(Boolean)) return null;
  return (
    <div className="mt-8 border border-gold/25 bg-gold/[0.03] p-6" data-testid="client-request-summary">
      <div className="flex items-center justify-between gap-4 mb-5">
        <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold">{tr("Request Summary")}</p>
        {summary.Status && (
          <span data-testid="client-status-badge" className={`text-[0.58rem] uppercase tracking-[0.2em] border px-3 py-1.5 ${CRM_STATUS_STYLES[summary.Status.split(" - ")[0]] || CRM_STATUS_STYLES["RECEIVED"]}`}>
            {displayStatus(summary.Status)}
          </span>
        )}
      </div>
      {summary.Service && <p className="font-serif text-lg font-light text-white/90 mb-5" data-testid="client-summary-service">{tr(summary.Service)}</p>}
      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        {[["Submitted", fmtSyncDate(summary.Submitted)],
          ["Service Date", fmtSyncDate(summary.Service_Date_1)],
          ["Location", summary.Service_Location],
          ["Budget", fmtSyncBudget(summary.Client_Budget)],
        ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
          <p key={label} className="text-sm font-light" data-testid={`client-summary-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
            <span className="text-white/80">{displayValue(value)}</span>
          </p>
        ))}
      </div>
      {summary.Latest_Update && (
        <div className="mt-5 pt-5 border-t border-gold/20" data-testid="client-delux-update">
          <p className="text-[0.6rem] uppercase tracking-[0.25em] text-gold/80 mb-2">{tr("Delux Update")}</p>
          <p className="text-white/75 text-sm font-light leading-relaxed">{summary.Latest_Update}</p>
        </div>
      )}
    </div>
  );
}

function RequestDetailModal({ request, onClose, onUpdated }) {
  if (!request) return null;
  return (
    <AnimatePresence>
      <motion.div
        data-testid="request-detail-modal"
        className="fixed inset-0 z-[70] flex items-center justify-center p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
        <motion.div
          className="modal-scroll relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0B0B0C]/95 backdrop-blur-xl border border-white/10 px-8 py-10 my-10"
          initial={{ y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <button onClick={onClose} data-testid="request-detail-close" className="absolute top-5 right-5 text-white/50 hover:text-gold transition-colors" aria-label={tr("Close")}>
            <X strokeWidth={1.25} size={22} />
          </button>
          <p className="overline-lux">{tr("Your Request")}</p>
          <h3 className="font-serif text-2xl font-light mt-3 pr-8">{displayRequestTitle(request)}</h3>
          <div className="flex items-center gap-4 mt-5">
            <span className={`text-[0.6rem] uppercase tracking-[0.22em] border px-3.5 py-1.5 ${STATUS_STYLES[request.status] || STATUS_STYLES["In Review"]}`}>
              {displayStatus(request.status)}
            </span>
            <span className="text-white/35 text-xs">
              {new Date(request.created_at).toLocaleDateString(locale(), { day: "numeric", month: "long", year: "numeric" })}
            </span>
          </div>
          <ClientRequestSummary summary={request.client_summary} />
          <div className="mt-8 border-t border-white/10 pt-7">
            {(request.service_date || request.location || request.guests || request.budget || request.preferred_time) && (
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 mb-7">
                {[["Service Date", request.service_date], ["Preferred Time", request.preferred_time], ["Location", request.location], ["Guests", request.guests], ["Budget", request.budget]].filter(([, v]) => v).map(([label, value]) => (
                  <p key={label} className="text-sm font-light">
                    <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                    <span className="text-white/80">{displayValue(value)}</span>
                  </p>
                ))}
              </div>
            )}
            <p className="text-[0.62rem] uppercase tracking-[0.3em] text-white/40 mb-3">{tr("Details")}</p>
            <p className="text-white/70 font-light leading-relaxed text-sm" data-testid="request-detail-message">{request.message}</p>
          </div>
          {requirementsPending(request) && (
            <div className="mt-7 border border-amber-400/50 bg-amber-400/5 p-5" data-testid="request-action-notice">
              <p className="text-amber-300 text-[0.62rem] uppercase tracking-[0.28em]">{tr("Action Required")}</p>
              <p className="text-white/60 text-xs font-light mt-2 leading-relaxed">{tr("Additional information is required for this request — your booking cannot proceed until the details below are completed.")}</p>
            </div>
          )}
          {request.requested_service === "Luxury Travel" && (
            request.travel_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="travel-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Luxury Travel Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Travel Type", request.travel_requirements.travel_type],
                    ["Departure", request.travel_requirements.date_of_departure],
                    ["Return", request.travel_requirements.date_of_return],
                    ["Nights", request.travel_requirements.number_of_nights],
                    ["Accommodation", request.travel_requirements.accommodation_type],
                    ["Rooms", request.travel_requirements.number_of_rooms],
                    ["Budget Per Night", request.travel_requirements.budget_per_night],
                    ["Flights Arranged By Us", request.travel_requirements.arrange_flights],
                    ["Flight Type", request.travel_requirements.flight_type],
                    ["Departure Airport", request.travel_requirements.departure_airport],
                    ["Arrival Airport", request.travel_requirements.arrival_airport],
                    ["Preferred Airlines", request.travel_requirements.preferred_airlines],
                    ["Ground Transportation", request.travel_requirements.arrange_ground],
                    ["Transportation", (request.travel_requirements.transportation_types || []).join(", ")],
                    ["Preferred Vehicle", request.travel_requirements.preferred_vehicle],
                    ["Notes", request.travel_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <TravelRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Yacht Charter" && (
            request.yacht_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="yacht-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Yacht Charter Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Cruising Area / Itinerary", request.yacht_requirements.cruising_area],
                    ["Charter Start", request.yacht_requirements.charter_start],
                    ["Charter End", request.yacht_requirements.charter_end],
                    ["Duration", request.yacht_requirements.charter_days ? `${request.yacht_requirements.charter_days} days` : ""],
                    ["Preferred Yacht Size", request.yacht_requirements.yacht_size],
                    ["Yacht Type / Style", request.yacht_requirements.yacht_style],
                    ["Onboard Requests", request.yacht_requirements.onboard_requests],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <YachtRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Private Jet Charter" && (
            request.jet_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="jet-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Jet Charter Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Trip Type", request.jet_requirements.trip_type],
                    ["Departure", request.jet_requirements.date_of_departure],
                    ["Return", request.jet_requirements.date_of_return],
                    ["Departure Airport", request.jet_requirements.departure_airport],
                    ["Arrival Airport", request.jet_requirements.arrival_airport],
                    ["Luggage", request.jet_requirements.number_of_luggage],
                    ["Preferred Aircraft", request.jet_requirements.preferred_aircraft_type],
                    ["Aircraft Category", request.jet_requirements.aircraft_category],
                    ["Cabin Crew", request.jet_requirements.cabin_crew],
                    ["Catering / Amenities", request.jet_requirements.catering],
                    ["Ground Transport", request.jet_requirements.ground_transport],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <JetRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Security" && (
            request.security_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="security-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Security Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Type of Security", request.security_requirements.security_type],
                    ["Threat Level", (THREAT_LEVELS.find((t) => t.value === request.security_requirements.threat_level) || {}).label || request.security_requirements.threat_level],
                    ["Duration", request.security_requirements.duration],
                    ["Additional Information", request.security_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <SecurityRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Supercar Hire" && (
            request.supercar_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="supercar-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Super Car Hire Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Licence Issue Country", request.supercar_requirements.licence_country],
                    ["Rental Start", request.supercar_requirements.rental_start],
                    ["Rental End", request.supercar_requirements.rental_end],
                    ["Vehicles Required", request.supercar_requirements.vehicles_required],
                    ["Vehicle Make", request.supercar_requirements.vehicle_make],
                    ["Vehicle Model", request.supercar_requirements.vehicle_model],
                    ["Mileage", (MILEAGE_OPTIONS.find((m) => m.value === request.supercar_requirements.mileage) || {}).label || request.supercar_requirements.mileage],
                    ["Collection", request.supercar_requirements.collection],
                    ["Concierge Assistance", request.supercar_requirements.concierge_assistance],
                    ["Notes", request.supercar_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <SupercarRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Chauffeurs & Transfers" && (
            request.chauffeur_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="chauffeur-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Chauffeur & Transfers Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Trip Type", request.chauffeur_requirements.trip_type],
                    ["Date of Pick-Up", request.chauffeur_requirements.date_of_pickup],
                    ["Number of Hours", request.chauffeur_requirements.number_of_hours],
                    ["Flight Number", request.chauffeur_requirements.flight_number],
                    ["Pick-Up Address", request.chauffeur_requirements.pickup_address],
                    ["Drop-Off Address", request.chauffeur_requirements.dropoff_address],
                    ["Return Trip", request.chauffeur_requirements.return_trip],
                    ["Date of Return", request.chauffeur_requirements.date_of_return],
                    ["Return Pick-Up Address", request.chauffeur_requirements.return_pickup_address],
                    ["Luggage", request.chauffeur_requirements.number_of_luggage],
                    ["Luggage Size", request.chauffeur_requirements.luggage_size],
                    ["Vehicle", request.chauffeur_requirements.vehicle],
                    ["Special Requests", request.chauffeur_requirements.special_requests],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <ChauffeurRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Events & Experiences" && (
            request.events_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="events-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Events & Experiences Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Event Type", request.events_requirements.event_type],
                    ["Other Event Type", request.events_requirements.other_event_type],
                    ["Date of Event", request.events_requirements.date_of_event],
                    ["Event Requirements", request.events_requirements.event_details],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <EventsRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Health & Wellness" && (
            request.wellness_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="wellness-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Health & Wellness Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Type of Service", request.wellness_requirements.service_type],
                    ["Other Service", request.wellness_requirements.other_service],
                    ["Gender Preference", request.wellness_requirements.gender_preference],
                    ["Special Requests", request.wellness_requirements.special_requests],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <WellnessRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Daily Needs" && (
            request.daily_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="daily-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Daily Needs Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Required Service", request.daily_requirements.required_service],
                    ["Working Arrangement", request.daily_requirements.working_arrangement],
                    ["Start Date", request.daily_requirements.start_date],
                    ["End Date", request.daily_requirements.end_date],
                    ["Number of Hours", request.daily_requirements.number_of_hours],
                    ["Special Requests", request.daily_requirements.special_requests],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <DailyNeedsRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Personal Shopping & Sourcing" && (
            request.shopping_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="shopping-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Personal Shopping Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Type of Shopping", request.shopping_requirements.shopping_type],
                    ["Preferred Brands", request.shopping_requirements.preferred_brands],
                    ["Preferred Stores", request.shopping_requirements.preferred_stores],
                    ["Style Preferences", request.shopping_requirements.style_preferences],
                    ["Budget Range", request.shopping_requirements.budget_range],
                    ["Frequency", request.shopping_requirements.frequency],
                    ["Shopping Location / Market", request.shopping_requirements.shopping_location],
                    ["Deadline", request.shopping_requirements.deadline],
                    ["Occasion", request.shopping_requirements.occasion === "YES" ? `YES${request.shopping_requirements.occasion_detail ? ` — ${request.shopping_requirements.occasion_detail}` : ""}` : "NO"],
                    ["Notes", request.shopping_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <ShoppingRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Art & Collectibles Access" && (
            request.art_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="art-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Art & Collectibles Access Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Type of Art Requested", request.art_requirements.art_type],
                    ["Preferred Period / Style", request.art_requirements.period_style],
                    ["Purpose of Acquisition", request.art_requirements.purpose],
                    ["Other Purpose", request.art_requirements.purpose_other],
                    ["Preferred Artists", request.art_requirements.artists],
                    ["Acquisition Frequency", request.art_requirements.frequency],
                    ["Sourcing / Viewing Region", request.art_requirements.region],
                    ["Delivery / Shipping", request.art_requirements.delivery_method],
                    ["Collection / Acquisition Location", request.art_requirements.collection_location],
                    ["Confidentiality", request.art_requirements.confidentiality],
                    ["Art Advisor Required", request.art_requirements.advisor_required],
                    ["Acquisition Budget Range", request.art_requirements.budget_range],
                    ["Special Requests", request.art_requirements.special_requests],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <ArtRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Global Lounge / Private Terminal Access" && (
            request.lounge_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="lounge-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Global Lounge / Private Terminal Access Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Purpose of Travel", (request.lounge_requirements.purpose || []).join(", ")],
                    ["Travel Frequency", request.lounge_requirements.frequency],
                    ["Travel Date", request.lounge_requirements.travel_date],
                    ["Meet & Greet", request.lounge_requirements.meet_greet],
                    ["Baggage Assistance", request.lounge_requirements.baggage_assistance],
                    ["Preferred Lounges or Operators", request.lounge_requirements.preferred_lounges],
                    ["Transfer Vehicle", request.lounge_requirements.transfer_vehicle],
                    ["Requested Vehicle", request.lounge_requirements.requested_vehicle],
                    ["Trip Co-ordination", request.lounge_requirements.trip_coordination],
                    ["Additional Requests", request.lounge_requirements.additional_requests],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <LoungeRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Elite Social Club Access" && (
            request.club_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="club-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Elite Social Club Access Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Type of Club Access", request.club_requirements.club_type],
                    ["Other Club Type", request.club_requirements.club_type_other],
                    ["Preferred Location / City", request.club_requirements.location_city],
                    ["Name of Club", request.club_requirements.club_name],
                    ["Purpose of Visit", request.club_requirements.purpose],
                    ["Special Requests", request.club_requirements.special_requests],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <ClubRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Fine Dining" && (
            request.dining_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="dining-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Fine Dining Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Recommendations Requested", request.dining_requirements.recommendations],
                    ["Date & Time of Reservation", request.dining_requirements.reservation_datetime ? request.dining_requirements.reservation_datetime.replace("T", " at ") : ""],
                    ["Preferred Location / Area", request.dining_requirements.location_area],
                    ["Name of Restaurant", request.dining_requirements.restaurant_name],
                    ["Number of Diners", request.dining_requirements.number_of_diners],
                    ["Allergies / Dietary", request.dining_requirements.allergies],
                    ["Dining Style", request.dining_requirements.dining_style],
                    ["Occasion", request.dining_requirements.occasion === "YES" ? `YES${request.dining_requirements.occasion_detail ? ` — ${request.dining_requirements.occasion_detail}` : ""}` : request.dining_requirements.occasion],
                    ["Special Requests", request.dining_requirements.special_requests],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <DiningRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Jewellery Sourcing" && (
            request.jewellery_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="jewellery-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Jewellery Sourcing Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Type of Jewellery", request.jewellery_requirements.jewellery_type],
                    ["Preferred Brands / Designers", request.jewellery_requirements.brands_designers],
                    ["Style / Specifications", request.jewellery_requirements.style_specifications],
                    ["Occasion or Purpose", request.jewellery_requirements.occasion_purpose],
                    ["Other Occasion / Purpose", request.jewellery_requirements.occasion_other],
                    ["Budget Range", request.jewellery_requirements.budget_range],
                    ["Delivery Method", request.jewellery_requirements.delivery_method],
                    ["Notes / Special Requirements", request.jewellery_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <JewelleryRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Import & Export Management" && (
            request.import_export_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="import-export-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Import & Export Management Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Type of Good", request.import_export_requirements.goods_type],
                    ["Description of Item", request.import_export_requirements.description],
                    ["Quantity", request.import_export_requirements.quantity],
                    ["Declared Value", request.import_export_requirements.declared_value],
                    ["Collection / Origin", request.import_export_requirements.origin_location],
                    ["Delivery Destination", request.import_export_requirements.delivery_destination],
                    ["Transport Method", request.import_export_requirements.transport_method],
                    ["Has Documentation", request.import_export_requirements.documentation],
                    ["Security Level", request.import_export_requirements.security_level],
                    ["Notes", request.import_export_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
                {(request.import_export_documents || []).length > 0 && (
                  <div className="mt-5 pt-5 border-t border-white/10" data-testid="import-export-documents-done">
                    <p className="text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-2">{tr("Documents Received")}</p>
                    <p className="text-sm font-light text-white/80">{request.import_export_documents.map((d) => d.filename).join(", ")}</p>
                  </div>
                )}
              </div>
            ) : (
              <ImportExportRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Home Automation & Estate Technology" && (
            request.home_automation_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="home-automation-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Home Automation & Estate Technology Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Property Type", request.home_automation_requirements.property_type],
                    ["Other Property Type", request.home_automation_requirements.property_type_other],
                    ["Estimated Property Size", request.home_automation_requirements.property_size],
                    ["Scope of Work", request.home_automation_requirements.scope_of_work],
                    ["Installation Type", request.home_automation_requirements.installation_type],
                    ["Other Installation Type", request.home_automation_requirements.installation_type_other],
                    ["Budget", request.home_automation_requirements.budget],
                    ["Special Requests", request.home_automation_requirements.special_requests],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <HomeAutomationRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Private Education & Tutoring" && (
            request.education_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="education-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Private Education & Tutoring Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Education Type", request.education_requirements.education_type],
                    ["Education Level", request.education_requirements.education_level],
                    ["Other Education Level", request.education_requirements.education_level_other],
                    ["Student Age", request.education_requirements.student_age],
                    ["Preferred Tutor Gender", request.education_requirements.tutor_gender],
                    ["Preferred Subjects", request.education_requirements.subjects],
                    ["Preferred Study Location", request.education_requirements.study_location],
                    ["Preferred Start Date", request.education_requirements.start_date],
                    ["Frequency / Schedule", request.education_requirements.frequency_schedule],
                    ["Preferred Institutes", request.education_requirements.institutes],
                    ["Budget", request.education_requirements.budget],
                    ["Additional Notes", request.education_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <EducationRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Corporate Flight Coordination" && (
            request.corp_flight_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="corp-flight-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Corporate Flight Coordination Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Travel Purpose", request.corp_flight_requirements.travel_purpose],
                    ["Other Purpose", request.corp_flight_requirements.travel_purpose_other],
                    ["Type of Travel", request.corp_flight_requirements.travel_type],
                    ["Other Travel Type", request.corp_flight_requirements.travel_type_other],
                    ["Departure Airport", request.corp_flight_requirements.departure_airport],
                    ["Arrival Airport", request.corp_flight_requirements.arrival_airport],
                    ["Departure", request.corp_flight_requirements.departure_datetime ? request.corp_flight_requirements.departure_datetime.replace("T", " at ") : ""],
                    ["Passengers", request.corp_flight_requirements.passengers],
                    ["Luggage", request.corp_flight_requirements.luggage],
                    ["Return Trip", request.corp_flight_requirements.return_trip],
                    ["Return Departure", request.corp_flight_requirements.return_departure_airport],
                    ["Return Arrival", request.corp_flight_requirements.return_arrival_airport],
                    ["Return Date / Time", request.corp_flight_requirements.return_datetime ? request.corp_flight_requirements.return_datetime.replace("T", " at ") : ""],
                    ["Ground Transportation", request.corp_flight_requirements.ground_transport],
                    ["Catering / Onboard", request.corp_flight_requirements.catering],
                    ["Passenger Details", request.corp_flight_requirements.passenger_details],
                    ["Additional Notes", request.corp_flight_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <CorpFlightRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Tailored Corporate Retreats" && (
            request.retreat_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="retreat-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Tailored Corporate Retreats Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Retreat Objective", request.retreat_requirements.objective],
                    ["Retreat Type", request.retreat_requirements.retreat_type],
                    ["Other Retreat Type", request.retreat_requirements.retreat_type_other],
                    ["Destination", request.retreat_requirements.destination],
                    ["Preferred Dates", [request.retreat_requirements.date_from, request.retreat_requirements.date_to].filter(Boolean).join(" to ")],
                    ["Number of Attendees", request.retreat_requirements.attendees],
                    ["Accommodation Required", request.retreat_requirements.accommodation_required],
                    ["Accommodation Preference", request.retreat_requirements.accommodation_preference],
                    ["Other Accommodation", request.retreat_requirements.accommodation_other],
                    ["Meeting / Conference Facilities", request.retreat_requirements.meeting_facilities],
                    ["Transportation", request.retreat_requirements.transportation],
                    ["Budget", request.retreat_requirements.budget],
                    ["Dietary / Special Requirements", request.retreat_requirements.dietary],
                    ["Additional Notes", request.retreat_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <RetreatRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Relocations / Removals" && (
            request.relocation_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="relocation-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Relocations / Removals Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Type of Relocation", request.relocation_requirements.relocation_type],
                    ["Other Relocation Type", request.relocation_requirements.relocation_type_other],
                    ["Required Services", request.relocation_requirements.required_services],
                    ["Moving Date", request.relocation_requirements.moving_date],
                    ["Scope / Description", request.relocation_requirements.scope],
                    ["Current Address", request.relocation_requirements.current_address],
                    ["New Address", request.relocation_requirements.new_address],
                    ["Packing Required", request.relocation_requirements.packing],
                    ["Storage Required", request.relocation_requirements.storage],
                    ["Additional Notes", request.relocation_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
                {(request.relocation_inventory || []).length > 0 && (
                  <div className="mt-5 pt-5 border-t border-white/10" data-testid="relocation-inventory-done">
                    <p className="text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-2">{tr("Inventory Received")}</p>
                    <p className="text-sm font-light text-white/80">{request.relocation_inventory.map((d) => d.filename).join(", ")}</p>
                  </div>
                )}
              </div>
            ) : (
              <RelocationRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Networking Event Planning & Access" && (
            request.networking_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="networking-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Networking Event Planning & Access Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Event Objective / Purpose", request.networking_requirements.objective],
                    ["Event Format", request.networking_requirements.event_format],
                    ["Other Event Format", request.networking_requirements.event_format_other],
                    ["Preferred Event Date", request.networking_requirements.event_date],
                    ["Number of Attendees", request.networking_requirements.attendees],
                    ["Event Requirements", request.networking_requirements.requirements],
                    ["Existing Event Type", request.networking_requirements.existing_event_type],
                    ["Preferred Industry / Network", request.networking_requirements.industry],
                    ["Other Industry", request.networking_requirements.industry_other],
                    ["Event Budget", request.networking_requirements.budget],
                    ["Additional Notes", request.networking_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <NetworkingRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          {request.requested_service === "Employee & Client Incentives" && (
            request.incentive_requirements ? (
              <div className="mt-7 border border-gold/20 p-6 sm:p-8" data-testid="incentive-requirements-done">
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold mb-4">{tr("Employee & Client Incentives Requirements — Submitted")}</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[["Incentive Type", request.incentive_requirements.incentive_type],
                    ["Incentive Purpose", request.incentive_requirements.purpose],
                    ["Other Purpose", request.incentive_requirements.purpose_other],
                    ["Number of Participants", request.incentive_requirements.participants],
                    ["Frequency", request.incentive_requirements.frequency],
                    ["Preferred Dates", [request.incentive_requirements.date_from, request.incentive_requirements.date_to].filter(Boolean).join(" to ")],
                    ["Duration", request.incentive_requirements.duration],
                    ["Preferred Destination", request.incentive_requirements.destination],
                    ["Accommodation Required", request.incentive_requirements.accommodation_required],
                    ["Preferred Accommodation", request.incentive_requirements.accommodation],
                    ["Other Accommodation", request.incentive_requirements.accommodation_other],
                    ["Experiences / Activities", request.incentive_requirements.experiences],
                    ["Budget Per Person", request.incentive_requirements.budget_per_person],
                    ["Additional Services", request.incentive_requirements.additional_services],
                    ["Additional Notes", request.incentive_requirements.notes],
                  ].filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([label, value]) => (
                    <p key={label} className="text-sm font-light">
                      <span className="block text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-1">{tr(label)}</span>
                      <span className="text-white/80">{displayValue(value)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <IncentiveRequirementsForm request={request} onSubmitted={() => onUpdated && onUpdated(request.id)} />
            )
          )}
          <p className="mt-8 text-white/40 text-xs font-light leading-relaxed border-l border-gold/40 pl-4">{tr("Your advisor is reviewing this request and will be in touch. For anything urgent, contact your 24/7 advisor directly.")}</p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function MembershipCard({ user }) {
  const hasLongName = (user.name || "").length > 20;

  return (
    <div
      dir="ltr"
      data-testid="membership-card"
      aria-label={tr("Delux Concierge membership card")}
      className="relative aspect-[1.586/1] w-full overflow-hidden rounded-[20px] p-[7%] text-[#18324A] shadow-[0_24px_60px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.28),inset_0_-1px_0_rgba(86,52,12,0.18)]"
      style={{
        background: "radial-gradient(circle at 12% 0%, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0.08) 24%, transparent 42%), radial-gradient(circle at 100% 100%, rgba(91,55,13,0.22) 0%, rgba(91,55,13,0.08) 30%, transparent 52%), linear-gradient(132deg, #D9BB70 0%, #C79F4E 34%, #E1C67F 62%, #B8873A 100%)",
      }}
    >
      <div className="relative z-10 h-full">
        <img
          src="/images/delux-logo.png"
          alt={tr("Delux Concierge logo")}
          className="absolute -top-3 -left-3 h-[31%] w-auto max-w-[88px] object-contain"
          data-testid="membership-card-logo"
        />

        <div className="absolute left-0 right-0 bottom-0">
          <p
            data-testid="membership-card-name"
            title={user.name}
            className={`font-serif ${hasLongName ? "text-[1rem] leading-[1.05]" : "text-[clamp(1.16rem,1.55vw,1.58rem)] leading-none"} tracking-[0.12em] uppercase`}
            style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
          >
            {user.name}
          </p>
          <div className="mt-[3.2%] h-[1.5px] w-[72%] bg-[#18324A]/60" />
          <div className="mt-[4%]">
            <p className="text-[clamp(0.42rem,0.58vw,0.54rem)] tracking-[0.18em] text-[#18324A]/72 mb-[2%]">{tr("MEMBERSHIP ID")}</p>
            <p data-testid="membership-card-id" className={`font-serif ${hasLongName ? "text-[1rem]" : "text-[clamp(1.16rem,1.55vw,1.58rem)]"} leading-none tracking-[0.1em] font-normal`}>
              {user.member_id}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Portal() {
  const { user, setUser } = useAuth();
  const openLogin = useLoginModal();
  const navigate = useLangNavigate();
  const [overview, setOverview] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [viewRequest, setViewRequest] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const fetchOverview = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/overview`, { headers: authHeader() });
      setOverview(data);
      return data;
    } catch {
      localStorage.removeItem("dc_token");
      localStorage.removeItem("dc_user");
      setUser(null);
      navigate("/");
    }
  };

  const refreshRequest = async (id) => {
    const data = await fetchOverview();
    const updated = data?.requests?.find((x) => x.id === id);
    if (updated) setViewRequest(updated);
  };

  const openRequestCard = (r) => {
    setViewRequest(r);
    if (r.has_update) {
      axios
        .post(`${API}/portal/service-requests/${r.id}/mark-seen`, {}, { headers: authHeader() })
        .then(() => {
          fetchOverview();
          window.dispatchEvent(new Event("dc-updates-changed"));
        })
        .catch(() => {});
    }
  };

  const openRequestById = async (id) => {
    const data = await fetchOverview();
    const updated = data?.requests?.find((x) => x.id === id);
    if (updated) setViewRequest(updated);
  };

  useEffect(() => {
    const token = localStorage.getItem("dc_token");
    if (!user || !token) {
      navigate("/");
      openLogin();
      return;
    }
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => {
    localStorage.removeItem("dc_token");
    localStorage.removeItem("dc_user");
    setUser(null);
    navigate("/");
  };

  if (!user) return null;
  const requests = overview?.requests || [];
  const isCompleted = (r) => (r.client_summary?.Status || "").toUpperCase().startsWith("COMPLETED");
  const activeRequests = requests.filter((r) => !isCompleted(r));
  const pastBookings = requests.filter(isCompleted);
  const tierServices = TIER_SERVICES[user.tier] || TIER_SERVICES.Individual;

  const DETAIL_ROWS = [
    ["Membership Tier", tr(user.tier)],
    ["Member ID", user.member_id],
    ["Full Name", user.name],
    ["Email", user.email],
    ["Phone", user.phone || "—"],
    ["Membership Status", tr(user.status || "Active")],
  ];

  return (
    <div data-testid="portal-page" className="min-h-screen pt-36 pb-24">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>{tr("Member Portal")}</Overline>
            <h1 className="font-serif font-light text-4xl sm:text-5xl mt-4">{tr("Welcome back,")}{" "}<em className="gold-shimmer italic">{user.name.split(" ")[0]}.</em>
            </h1>
          </div>
          <button
            onClick={signOut}
            data-testid="portal-signout-button"
            className="flex items-center gap-2.5 border border-white/20 text-white/60 text-[0.65rem] uppercase tracking-[0.28em] px-6 py-3 hover:border-gold hover:text-gold transition-colors duration-300"
          >
            <LogOut strokeWidth={1.5} size={13} />{tr("Sign Out")}</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-14">
          {/* ——— YOUR DETAILS ——— */}
          <Reveal className="lg:col-span-5 self-start">
            <div className="border border-gold/40 bg-[#0B0A08] p-8 flex flex-col">
              <p className="overline-lux">{tr("Your Details")}</p>
              <div className="mt-7 mb-8">
                <MembershipCard user={user} />
              </div>
              <div className="mt-8 space-y-3.5 text-sm font-light text-white/55 flex-1">
                {DETAIL_ROWS.map(([label, value]) => (
                  <p key={label} className="flex justify-between gap-4 border-b border-white/10 pb-3.5 last:border-0">
                    <span>{tr(label)}</span>
                    <span
                      className={`text-right truncate ${label === "Membership Status" ? "text-gold" : "text-white/85"}`}
                      data-testid={label === "Member ID" ? "portal-member-id" : `portal-detail-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {value}
                    </span>
                  </p>
                ))}
              </div>
              <button
                onClick={() => setManageOpen(true)}
                data-testid="portal-manage-account"
                className="mt-8 border border-gold/70 text-gold text-[0.65rem] uppercase tracking-[0.28em] py-4 hover:bg-gold hover:text-black transition-colors duration-400"
              >{tr("Manage Your Account")}</button>
            </div>
          </Reveal>

          {/* ——— YOUR REQUESTS ——— */}
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="border border-white/10 p-8">
              <div className="flex items-center justify-between mb-8">
                <p className="overline-lux">{tr("Your Requests")}</p>
                <button
                  onClick={() => setRequestOpen(true)}
                  data-testid="portal-new-request"
                  className="flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.28em] text-gold hover:text-gold-bright transition-colors duration-300"
                >
                  <Plus strokeWidth={1.5} size={13} />{tr("Request Service")}</button>
              </div>
              {activeRequests.length === 0 ? (
                <p className="text-white/40 font-light text-sm py-8" data-testid="portal-no-requests">
                  {pastBookings.length > 0 ? tr("No active requests — your advisor is ready when you are.") : tr("No requests yet — your advisor is ready when you are.")}
                </p>
              ) : (
                <ul className="divide-y divide-white/10">
                  {activeRequests.map((r) => (
                    <li key={r.id} data-testid={`portal-request-${r.id}`} className="py-5 flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="text-white/85 font-light">{displayRequestTitle(r)}</p>
                        <p className="text-white/40 text-xs font-light mt-1 max-w-md">{r.message}</p>
                        <p className="text-white/30 text-xs mt-2">
                          {new Date(r.created_at).toLocaleDateString(locale(), { day: "numeric", month: "short", year: "numeric" })}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2.5 shrink-0">
                        <span
                          data-testid={`portal-request-status-${r.id}`}
                          className={`text-[0.6rem] uppercase tracking-[0.22em] border px-3.5 py-1.5 ${STATUS_STYLES[r.status] || STATUS_STYLES["In Review"]}`}
                        >
                          {displayStatus(r.status)}
                        </span>
                        {requirementsPending(r) && (
                          <span data-testid={`portal-request-action-${r.id}`} className="text-[0.6rem] uppercase tracking-[0.22em] border border-amber-400/60 text-amber-300 px-3.5 py-1.5">{tr("Action Required")}</span>
                        )}
                        <button
                          data-testid={`portal-view-request-${r.id}`}
                          onClick={() => openRequestCard(r)}
                          className={`relative text-[0.6rem] uppercase tracking-[0.25em] transition-colors duration-300 border-b pb-0.5 ${
                            r.has_update
                              ? "text-red-400 border-red-400/40 hover:text-red-300 hover:border-red-300 pr-3.5"
                              : "text-white/50 hover:text-gold border-white/20 hover:border-gold"
                          }`}
                        >
                          {r.has_update ? tr("Update Available — View Now") : tr("View Request")}
                          {r.has_update && (
                            <span data-testid={`portal-update-dot-${r.id}`} className="absolute -top-1 -right-0 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                          )}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Reveal>
        </div>

        {/* ——— LUCAS GRANT ——— */}
        <Reveal delay={0.12} className="mt-6">
          <div className="relative overflow-hidden border border-gold/25 bg-[#0B0A08] p-8 lg:p-10" data-testid="lucas-portal-card">
            <div className="absolute -right-20 -top-24 w-72 h-72 rounded-full bg-gold/10 blur-3xl" />
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-full border border-gold/50 bg-gold/10 flex items-center justify-center text-gold font-serif text-2xl shrink-0">LG</div>
                <div>
                  <p className="overline-lux">{tr("Private Assistant")}</p>
                  <h2 className="font-serif font-light text-4xl mt-3">{tr("Lucas Grant")}</h2>
                  <p className="text-white/48 font-light text-sm leading-relaxed mt-4 max-w-2xl">
                    {tr("Speak with Lucas to arrange any service available to your membership. He will gather the required details, review everything with you, and only submit once you confirm.")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAssistantOpen(true)}
                data-testid="portal-open-lucas"
                className="bg-gold text-black text-[0.65rem] uppercase tracking-[0.28em] px-8 py-4 hover:bg-gold-bright transition-colors duration-300 shrink-0"
              >
                {tr("Speak with Lucas")}
              </button>
            </div>
          </div>
        </Reveal>

        {/* ——— PAST BOOKINGS ——— */}
        {pastBookings.length > 0 && (
          <Reveal delay={0.1} className="mt-6">
            <div className="border border-white/10 p-8" data-testid="portal-past-bookings">
              <div className="flex items-center justify-between mb-6">
                <p className="overline-lux">{tr("Past Bookings")}</p>
                <span className="text-[0.6rem] uppercase tracking-[0.25em] text-white/30" data-testid="portal-past-count">
                  {tr("{{total}} completed", { total: pastBookings.length })}</span>
              </div>
              <ul className="divide-y divide-white/[0.07]">
                {pastBookings.map((r) => (
                  <li key={r.id} data-testid={`portal-past-${r.id}`} className="py-5 flex flex-wrap items-center justify-between gap-4 opacity-60 hover:opacity-100 transition-opacity duration-300">
                    <div>
                      <p className="text-white/75 font-light">{displayRequestTitle(r)}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-[0.58rem] uppercase tracking-[0.2em] text-white/45 border border-white/15 px-2.5 py-1">
                          {displayStatus(r.client_summary?.Status, "COMPLETED")}
                        </span>
                        <span className="text-white/35 text-xs">
                          {new Date(r.created_at).toLocaleDateString(locale(), { day: "numeric", month: "long", year: "numeric" })}
                        </span>
                      </div>
                    </div>
                    <button
                      data-testid={`portal-view-past-${r.id}`}
                      onClick={() => openRequestCard(r)}
                      className="text-[0.6rem] uppercase tracking-[0.25em] text-white/45 hover:text-gold transition-colors duration-300 border-b border-white/15 hover:border-gold pb-0.5"
                    >{tr("View Booking")}</button>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        )}

        {/* ——— SERVICES AVAILABLE TO YOU ——— */}
        <Reveal delay={0.1} className="mt-6">
          <div className="border border-white/10 p-8 lg:p-10" data-testid="portal-services">
            <p className="overline-lux">{tr("Services Available To You")}</p>
            <p className="text-white/45 font-light text-sm mt-4 max-w-2xl">{tr("As an {{tier}} member, your advisor can arrange our full flagship collection plus your tier-exclusive services below.", { tier: tr(user.tier) })}</p>
            <p className="text-[0.62rem] uppercase tracking-[0.3em] text-white/40 mt-10">{tr("Flagship Collection — available to all members")}</p>
            <div className="flex flex-wrap gap-2.5 mt-4">
              {FLAGSHIP_SERVICES.map((s) => (
                <span key={s} data-testid={`portal-flagship-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="border border-gold/35 text-gold/90 text-[0.62rem] uppercase tracking-[0.22em] px-4 py-2">
                  {tr(s)}
                </span>
              ))}
            </div>
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold/80 mb-6">{tr("{{tier}} Membership — exclusive services", { tier: tr(user.tier) })}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                {tierServices.map((s) => (
                  <p key={s} data-testid={`portal-tier-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-start gap-3 text-white/65 font-light text-sm">
                    <Check strokeWidth={1.5} size={15} className="text-gold shrink-0 mt-0.5" />
                    {tr(s)}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* ——— YOUR MEMBERSHIP ——— */}
        <Reveal delay={0.15} className="mt-6">
          <div className="border border-white/10" data-testid="portal-membership-box">
            <p className="overline-lux px-8 pt-8">{tr("Your Membership")}</p>
            <div className="divide-y divide-white/10 mt-6">
              <Link to="/contact" data-testid="membership-contact-advisor" className="group flex items-center gap-6 px-8 py-7 hover:bg-white/[0.03] transition-colors duration-300">
                <PhoneCall strokeWidth={1.25} size={20} className="text-gold shrink-0" />
                <div className="flex-1">
                  <p className="font-serif text-xl font-light">{tr("Contact Your 24/7 Advisor")}</p>
                  <p className="text-white/40 text-xs font-light mt-1">{tr("Speak directly with your dedicated advisor, day or night.")}</p>
                </div>
                <span className="text-white/30 group-hover:text-gold transition-colors duration-300">→</span>
              </Link>
              <button onClick={() => setAction("amend_tier")} data-testid="membership-amend-tier" className="group w-full flex items-center gap-6 px-8 py-7 text-left hover:bg-white/[0.03] transition-colors duration-300">
                <PencilLine strokeWidth={1.25} size={20} className="text-gold shrink-0" />
                <div className="flex-1">
                  <p className="font-serif text-xl font-light">{tr("Amend Your Membership Tier")}</p>
                  <p className="text-white/40 text-xs font-light mt-1">{tr("Move between Individual and Corporate membership.")}</p>
                </div>
                <span className="text-white/30 group-hover:text-gold transition-colors duration-300">→</span>
              </button>
              <button onClick={() => setAction("cancel")} data-testid="membership-cancel" className="group w-full flex items-center gap-6 px-8 py-7 text-left hover:bg-white/[0.03] transition-colors duration-300">
                <XCircle strokeWidth={1.25} size={20} className="text-white/40 shrink-0" />
                <div className="flex-1">
                  <p className="font-serif text-xl font-light text-white/70">{tr("Cancel Your Membership")}</p>
                  <p className="text-white/40 text-xs font-light mt-1">{tr("Begin the cancellation process — an advisor will confirm with you.")}</p>
                </div>
                <span className="text-white/30 group-hover:text-gold transition-colors duration-300">→</span>
              </button>
            </div>
          </div>
        </Reveal>

        {/* ——— PERKS ——— */}
        <Reveal delay={0.2} className="mt-6">
          <div className="border border-white/10 p-8 grid sm:grid-cols-3 gap-10">
            {PERKS.map((p) => (
              <div key={p.title} className="flex gap-5">
                <p.icon strokeWidth={1} size={24} className="text-gold shrink-0 mt-1" />
                <div>
                  <p className="text-[0.65rem] uppercase tracking-[0.25em] text-white">{tr(p.title)}</p>
                  <p className="text-white/45 text-xs font-light mt-2 leading-relaxed">{tr(p.text)}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      <ManageModal open={manageOpen} onClose={() => setManageOpen(false)} />
      <MembershipActionModal action={action} onClose={() => setAction(null)} />
      <ServiceRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} onCreated={fetchOverview} />
      <RequestDetailModal request={viewRequest} onClose={() => setViewRequest(null)} onUpdated={refreshRequest} />
      <LucasAssistant
        open={assistantOpen}
        setOpen={setAssistantOpen}
        user={user}
        onRequestCreated={(request) => openRequestById(request.id)}
        onOpenRequest={openRequestById}
      />
    </div>
  );
}
